// The API contract the pages use, in one place. It follows what the server
// sends and accepts (crates/poulpe-server/src: feed.rs, mutations.rs,
// error.rs, the ops in board/ and people/), with poulpe-model's types as they
// go over the wire in JSON.
//
//   POST /v1/mutations             {mutation_id, op, args} → {mutation_id, seq, result}
//   GET  /v1/changes?since&limit[&epoch]   {changes, cursor}; 410 `snapshot_needed` when the feed cannot continue
//   GET  /v1/snapshot              {seq, projects, labels, issues, record_entries, questions, sessions, proposals, landings, plans, runs}
//   GET  /v1/digest?project&since&limit   bounded outcome groups, run count and reported cost
//   GET  /v1/plans/{id}            a plan with its body, its revisions and its comments; 404 for none
//   GET  /v1/issues/{id}/revisions         the descriptions an issue replaced, newest first, without their texts
//   GET  /v1/issues/{id}/revisions/{n}     one of them, with its text
//
// Agents' sessions run on the workspace's runners (Poulpe-340;
// crates/poulpe-server/src: board/sessions.rs, transcript.rs,
// runners/registration.rs). Their ops go through /v1/mutations as every
// other; their transcripts come apart, as do the runners, and the one-time
// code a person makes, from their session, to register one:
//
//   GET  /v1/runners                          [Runner], with what each offers
//   POST /v1/runners/codes                    no body, with a confirmation of `register_runner` → {code, expires_at}
//   GET  /v1/sessions/{id}/transcript?after   {items, more}: at most 100 items and 1 MiB, oldest first
//   GET  /v1/sessions/{id}/transcript?before  {items, more, older}: the newest page before an item, or `end`
//   GET  /v1/sessions/{id}/transcript?after   upgraded to a WebSocket: the items after
//                                             `after`, then each new one (api/transcript.ts)
//
// People sign in with passkeys (people/mod.rs). A ceremony is two requests:
//
//   POST /v1/auth/signup/start     {name, workspace} → {ceremony, options: {publicKey}}
//   POST /v1/auth/signup/finish    {ceremony, credential} → the body of /v1/me, and the session's cookie
//   POST /v1/auth/signin/start     no body → {ceremony, options: {publicKey, mediation?}}
//   POST /v1/auth/signin/finish    {ceremony, credential} → as signup/finish
//   POST /v1/auth/signout          204, and the cookie cleared
//   GET  /v1/me                    {actor, member: {id, name} | null, workspace: {id, name}, plan_format}
//   POST /v1/me                    person with set_profile/read: {name?, workspace?, plan_format?} → updated /v1/me
//   POST /v1/auth/confirm/start    {action: {kind, digest}} → {ceremony, options: {publicKey}}
//   POST /v1/auth/confirm/finish   {ceremony, credential} → {action, expires_at}
//   POST /v1/auth/signup/status    no body → {closed}
//
// Who may sign up is the server's setting (people/signup.rs): anyone, or the
// server's first person alone. A sign-up it refuses, when it starts or when
// it finishes, is a 403 `signup_closed`.
//
// Changing a policy takes kind `set_project_policy`, bound to the project
// and every policy field by `settings/policy.ts` (including its version).
// A confirmation is a fresh passkey assertion for exactly one action, a kind
// and the SHA-256 of what it approves (people/confirmations.rs). The server
// takes it, once, within five minutes, where the action is taken: a reserved
// landing's `approve_landing` takes one of kind `approve_landing` and the
// digest of its subject (queue/subject.ts); a runner's code, one of kind
// `register_runner` and the SHA-256 of the empty string, since the
// confirmation names its person and workspace itself (runners/registration.ts).
// A code is made from a session alone: a bearer token, a person's included,
// is refused, 401.
//
// The options are what `navigator.credentials.create()` or `.get()` takes, as
// JSON; the credential is the browser's `PublicKeyCredential`, as its
// `toJSON()` writes it (api/passkeys.ts). A refused ceremony is a 403 with
// the reason `passkey_refused`, whatever went wrong.
//
// A request is signed in by the session's cookie (`poulpe_session`,
// HttpOnly), which the browser sends to its own origin, or by
// `Authorization: Bearer <token>`, which wins over the cookie. Without
// either, or with one that ended, the answer is 401. A request signed in by
// the cookie, other than a GET, must come from the web app's own origin, or
// it is refused (403 `cross_origin`); the browser's `Origin` header says so.
// A refusal is an HTTP error whose body is `{error: <code>, message}`, with
// `reason: <code>` when one of the model's or the server's rules refused it.
// An edit of a part that names its base and cannot merge adds the part and
// both texts (TextConflictBody).

import type { PlanLimitsReply } from './planLimits'

export type IsoTime = string

// --- The board's entities ------------------------------------------------------------

export type StateSlug = 'backlog' | 'todo' | 'in_progress' | 'on_main' | 'done' | 'cancelled' | 'duplicate'

export type Priority = 'none' | 'urgent' | 'high' | 'medium' | 'low'

/** An issue's three short parts, for people. */
export type Part = 'description' | 'plan' | 'outcome'

export interface Project {
  id: string
  /** What its issues' keys start with: `Poulpe` in `Poulpe-331`. */
  key: string
  name: string
  /** The GitHub repository its work lands in, `owner/name`; null until a person sets it (`set_project_repository`): it lands nowhere until then. */
  repository: string | null
  /** The branch its work lands on: `main` unless a person set another. */
  branch: string
  /** The agent its PM agent runs as: Claude, on any runner of the project, unless a person set another (`set_pm_agent`). */
  pm_agent: PmAgent
  /** Which paths each of its release channels ships, and their tag prefixes (`set_release_map`, releases.rs); null or absent: none, and its issues stay On Main for a person. */
  release_map?: ReleaseMap | null
  /** The columns of its own its board adds to the fixed states, in the order the board shows them (`set_project_columns`). Absent from an older server. */
  columns?: ProjectColumn[]
  /** Current policy; absent only in a cache made before Flow settings. */
  landing_policy?: LandingPolicy
  /** The limits a worker started in it takes unless its lead names others (`set_worker_limits`, Poulpe-486); null or absent: Poulpe's own, `WORKER_LIMITS_DEFAULT`. */
  worker_limits?: WorkerLimits | null
}

/** A worker's limits (board/limits.rs): working minutes pause at questions and between turns; lifetime runs from its start; the token warning only warns. */
export interface WorkerLimits {
  working_minutes: number
  lifetime_minutes: number
  token_warning: number
}

/** Poulpe's own worker limits, while a project sets none (`WorkerLimits::default`). */
export const WORKER_LIMITS_DEFAULT: WorkerLimits = { working_minutes: 120, lifetime_minutes: 240, token_warning: 200_000 }
/** The server's bounds (`limits::MOST`, `TOKENS_MOST`): minutes 1–100,000, lifetime at least the working time, a token warning 1–1,000,000,000,000. */
export const WORKER_MINUTES_MOST = 100_000
export const WORKER_TOKENS_MOST = 1_000_000_000_000

/** The server's versioned policy. Reviewers always run on the proposing runner. */
export interface LandingPolicy {
  version: number
  reviewer?: { agent: string; model: string | null } | null
  cross_vendor_review: boolean
  /**
   * Who reviews: an agent, when absent (a policy saved before people could
   * review leaves it out), or a person, who gives the verdict in Ready to
   * merge for the exact head, whatever `cross_vendor_review` says (Poulpe-688).
   */
  review?: 'agent' | 'person'
  person_paths: string[]
  check_paths: string[]
  checks_need_person: boolean
  max_batch_wait: { secs: number; nanos: number } | null
  /** What the queue checks of the exact candidate (Poulpe-685); the server leaves it out when full. */
  check_scope?: CheckScope
}

/** Every part, or the parts the candidate's changes since main affect. */
export type CheckScope = 'full' | 'affected'

export const defaultLandingPolicy = (): LandingPolicy => ({
  version: 1, reviewer: null, cross_vendor_review: true, person_paths: [], check_paths: [], checks_need_person: true, max_batch_wait: null, check_scope: 'full',
})

/** A project's release map (poulpe_model::ReleaseMap): its channels, and the paths that need no release. Paths are landing-policy globs. */
export interface ReleaseMap {
  channels: { name: string; tag: string | null; paths: string[] }[]
  no_release: string[]
}

/** The kind of state a column of a project's own stands for, by Linear's names. An issue in it is in the state its type maps to (`COLUMN_STATE`). */
export type ColumnType = 'backlog' | 'unstarted' | 'started' | 'completed' | 'canceled' | 'duplicate'

/** One of a project's own columns (poulpe-model, column.rs). Its type is fixed when it is made. */
export interface ProjectColumn {
  id: string
  /** 1 to 40 characters. */
  name: string
  type: ColumnType
}

/**
 * `set_project_columns`: a person sets the whole list, in the order the
 * board shows them (403 `columns_not_by_person` for an agent). A column
 * without an id is new. At most 12 columns (400 `too_many_columns`); names of
 * 1 to 40 characters (400), none twice nor a state's (409
 * `column_name_taken`); a column keeps its type (400 `column_type_changed`);
 * one that holds an issue is not left out (409 `column_not_empty`).
 */
export interface SetProjectColumnsArgs {
  project_id: string
  columns: { id?: string; name: string; type: ColumnType }[]
}

/**
 * The agent a project's PM agent runs as, the choice New session makes: an
 * agent by its name on the runner, one of its models or null for its own
 * default, and a runner or null for any runner of the project. Whether a
 * runner offers them is asked when a batch starts its PM agent.
 */
export interface PmAgent {
  agent: string
  model: string | null
  runner_id: string | null
}

/**
 * `set_pm_agent`: a person sets it (403 `pm_agent_not_by_person` for an
 * agent). The agent is a runner's name for it (400 otherwise), a model is
 * not blank, and a runner of another workspace is not found (404).
 */
export interface SetPmAgentArgs {
  project_id: string
  agent: string
  model?: string
  runner_id?: string
}

/** Who wrote something: a person, by their membership; an agent, with its model and session; or Poulpe itself. */
export type Actor =
  | { kind: 'person'; member: string }
  | { kind: 'agent'; agent: { vendor: string; model: string }; session: string }
  | { kind: 'poulpe' }

/** A person's approval of a plan. It holds for the text it approved: a revision drops it. */
export interface PlanApproval {
  /** The member who approved it. */
  by: string
  at: IsoTime
}

export interface Plan {
  /** Markdown. */
  text: string
  approval: PlanApproval | null
}

/**
 * One of a project's labels (poulpe-model, label.rs). Its name is unique
 * among the project's labels that are not deleted, whatever its case. People
 * make, rename, recolour and delete labels; people, and an agent on its own
 * session's issue, put them on issues and take them off. Deleting one is
 * final: it leaves every issue, changes no more, and its name is free again.
 */
export interface Label {
  id: string
  /** Its project's id: it goes on this project's issues alone. */
  project: string
  /** 1 to 60 characters, trimmed. */
  name: string
  /** `#rrggbb`, as given. */
  colour: string
  /** When it was deleted; null while it lives. */
  deleted_at: IsoTime | null
}

/**
 * Who works on an issue, and since when: a person, or an agent in its
 * session. A claim is information, not a lock: it stops no one from changing
 * the issue. Another's claim is refused while one is held (409 `claimed`).
 */
export interface Claim {
  by: Actor
  at: IsoTime
}

/** An issue, as the server sends it (board/issues.rs, IssueView). Its record comes apart, as entries. */
export interface Issue {
  id: string
  /** Its project's id. */
  project: string
  /** Its number in its project: 1, 2, 3… */
  number: number
  /** `Poulpe-001`: its project's key and its number in three digits at least. */
  key: string
  title: string
  state: StateSlug
  /** The column of its project's own it stands in, whose type maps to its state; null in the fixed column of its state. Absent from an older server. */
  column?: string | null
  priority: Priority
  /** Markdown. */
  description: string
  /** One line; ordinary edits allow 200 characters, imports preserve up to 255. Empty for none. */
  summary: string
  /** Original creator's display name, with no actor rights here. */
  historical_creator?: string
  /** Archived imports remain readable through links and leave ordinary columns. */
  archived_at?: IsoTime | null
  /** Who made it; null for an issue imported, or made before the board kept it. */
  created_by: Actor | null
  plan: Plan
  /** Markdown. */
  outcome: string
  /** Its labels' ids, all of its project's, in the order of their ids. */
  labels: string[]
  /** Who works on it now, if anyone says so. */
  claim: Claim | null
  /** Its place in its column (sortKey.ts): a column shows its issues from the smallest key, compared as plain strings. The server orders nothing. */
  sort_key: string
  created_at: IsoTime
  /** When its state last changed, or it was made. */
  state_changed_at: IsoTime
  /** When it last changed: its own fields, its labels or its claim. Its record, its questions and its sessions are not its own. */
  updated_at: IsoTime
  /** When it first entered In Progress; kept through later states, cleared back in Backlog or Todo. */
  started_at: IsoTime | null
  /** When it entered On Main or Done; cleared on leaving them. */
  completed_at: IsoTime | null
  /** When it entered Cancelled or Duplicate; cleared on leaving them. */
  cancelled_at: IsoTime | null
  /** The parts longer than their budget: flagged, never refused. */
  over_budget: Part[]
  /** Its relations to other issues, from its own side, by kind, then by the other's key (board/relations.rs). A change to one carries both issues on the feed. */
  relations: Relation[]
  /** The issue it is a sub-issue of, in its own project (board/parents.rs); null for none. Its sub-issues are the issues that name it. */
  parent: string | null
  /** The release channels its landed work touches, and those it awaits, in the map's order (releases.rs); Poulpe writes them, and the import sets an imported issue's (Poulpe-500). Its card and properties show them as chips (lib/releases.ts). */
  touches?: string[]
  awaits?: string[]
  /** The versions that released it on the board it was imported from, by surface (Poulpe-500, board/shipped.rs); absent for none. */
  released?: Released[]
  /** Its pull requests on the board it was imported from, oldest first (Poulpe-500); absent for none. */
  pulls?: PullRequest[]
}

/** A version that released an imported issue on the board it came from: `app 0.1.2`. */
export interface Released {
  surface: string
  version: string
}

/** A pull request of an imported issue on the board it came from: `sunplug/poulpe-legacy#155`. */
export interface PullRequest {
  repository: string
  number: number
}

/** What an issue is to another, from its own side: today's blocks / blocked by, related, and duplicate of / duplicated by. */
export type RelationKind = 'blocks' | 'blocked_by' | 'related' | 'duplicate_of' | 'duplicated_by'

/** One of an issue's relations, from its own side: what it is to the other issue, named by id and by key. The other may be of any project of the workspace. */
export interface Relation {
  kind: RelationKind
  /** The other issue's id. */
  issue: string
  /** The other issue's key. */
  key: string
}

export type EntryKind = 'note' | 'decision' | 'discussion' | 'session' | 'review' | 'question' | 'answer' | 'landing' | 'evidence' | 'handoff'

/** What a record entry points to. */
export interface Reference {
  kind: 'issue' | 'entry' | 'session' | 'question' | 'proposal' | 'commit' | 'receipt' | 'approval'
  id: string
}

/** One entry of an issue's record: typed, signed, and never changed once written (board/record.rs, EntryView). */
export interface RecordEntry {
  /** Stable source identity of imported history, independent of its kind or body. */
  import_source?: string
  id: string
  /** The issue whose record holds it. */
  issue: string
  /** Its place in the record: 1, 2, 3…, in the order entries were added. */
  number: number
  kind: EntryKind
  author: Actor
  /** Markdown. */
  body: string
  at: IsoTime
  /** Absent when it points to nothing. */
  refs?: Reference[]
  /**
   * The server wrote it for a question's lifecycle (Poulpe-408): asked, revised, reopened, answered, cancelled or sent
   * again. No request sets it. Absent otherwise, and on every entry written before it: such an entry proves nothing.
   */
  system?: true
  /** Source display facts; author remains the importer, with no source actor rights. */
  historical?: { author: string; at: IsoTime; source: string; payload: Record<string, unknown> }
}

/** One choice a prompt offers. */
export interface Choice {
  label: string
  /** What choosing it leads to. */
  description: string
  /** One choice per prompt at most. */
  recommended: boolean
}

/** What a question asks: two choices or more, or none for an answer in words only. A prompt with choices takes words too, unless it takes its choices only. */
export interface Prompt {
  text: string
  choices: Choice[]
  multi_select: boolean
  /** Only the choices answer it, not words, as an agent's permission: absent when false. */
  choices_only?: boolean
  /** What its words must be, as an agent's form field asks: absent for any words. */
  words?: Words
  /** A blank answer in words is refused, as for a field a form requires: absent when false. */
  required?: boolean
}

/** What a prompt's answer in words must be: any words, a finite number, or a whole number of 64 bits. */
export type Words = 'text' | 'number' | 'integer'

export type Target = { kind: 'person'; member: string } | { kind: 'pm' }

/** One reply in an answer, to the prompt in the same place: a choice by its index, several, or words. */
export type Reply = { kind: 'choice'; value: number } | { kind: 'choices'; value: number[] } | { kind: 'text'; value: string }

export interface Answer {
  by: Actor
  /** The version of the question it answers. */
  version: number
  replies: Reply[]
  at: IsoTime
}

export type QuestionState = 'open' | 'answered' | 'cancelled'

/** A question an agent's session asks, one card whoever asks (board/questions.rs, QuestionView). */
export interface Question {
  id: string
  /** The issue it is about, whose record keeps its life. */
  issue: string | null
  /** The session that asked it. */
  session: string
  /** From 1; every change to the open question raises it. An answer names the version it answers. */
  version: number
  /** The agent's own question, or Poulpe's `ask`. */
  kind: 'native' | 'ask'
  target: Target
  prompts: Prompt[]
  state: QuestionState
  answer: Answer | null
  /** Whether the answer has reached the agent. */
  delivery: 'none' | 'uncertain' | 'confirmed'
  delivery_revision: number
  /** Why the question was last reopened, written by the server: an answer its agent's side refused while the agent waits. Absent when it never was. */
  reopened?: string
  /** Whether its agent says it is urgent. */
  urgent: boolean
  /** Whether its agent waits for the answer, until its expiry if it has one; false when it went on without it. */
  blocking: boolean
  /** When its agent stops waiting, and what it goes on with then, in its own words. An expired question stays open and still takes an answer. Absent when it does not expire. */
  expiry?: Expiry
  /** When its agent last revised it. Absent when it never did. */
  revised_at?: IsoTime
}

/** When an agent that waits for an answer stops waiting (board/questions.rs, `expires_in_minutes`). */
export interface Expiry {
  at: IsoTime
  /** What it goes on with then, in its own words. */
  instead: string
}

// --- Agents' sessions ----------------------------------------------------------------------

/** `idle`: the agent waits for a prompt. */
export type SessionState = 'starting' | 'running' | 'idle' | 'ended'

/**
 * How a session began: started, from a commit once it is known; delegated by a parent session, from an exact commit; or
 * started by the landing queue to review a proposal, from the proposal's head.
 * A review's parent is its proposing session, for display; it cannot steer the review.
 */
export type SessionOrigin =
  | { kind: 'started'; base: string | null }
  | { kind: 'delegated'; parent: string; base: string }
  | { kind: 'review'; proposal: string; parent?: string | null; base: string }

/** How a session ended. `interrupted`: its runner stopped while it ran, for a restart (Poulpe-425). */
export interface SessionEnd {
  outcome: 'succeeded' | 'failed' | 'unknown' | 'interrupted'
  /** Why, when the server says. */
  reason?: string
}

/** What the runner last read of a session's work tree, against its base commit. */
export interface ChangedFile {
  path: string
  old_path?: string
  status: 'A' | 'M' | 'D' | 'R' | '?'
  additions: number | null
  deletions: number | null
  binary: boolean
  has_diff: boolean
}

export interface SessionChanges {
  at: IsoTime
  base: string
  truncated: boolean
  files: ChangedFile[]
}

export interface SessionChangesDiff {
  path: string
  diff: string
}

/**
 * The kept copy of a proposal's diff, from its base to its head, in the shape
 * of a session's changes (`GET /v1/proposals/{id}/changes`). Until the copy is
 * in, `changes` is null and `asked` names the job that asks the proposing
 * session's runner for it: read again shortly. Once the asks are spent,
 * `failure` says why the last one failed, and nothing more is asked.
 */
export interface ProposalChanges {
  proposal: string
  head: string
  changes: SessionChanges | null
  asked: string | null
  failure: string | null
}

/** One file's diff, from a proposal's kept copy; 404 without a copy, for an unknown path or a binary file. */
export type ProposalChangesDiff = SessionChangesDiff

/** One agent, on one runner, working on one project and perhaps one issue. */
export type SessionPlace = { kind: 'worktree' } | { kind: 'checkout' } | { kind: 'existing'; session: string }

export interface SessionBudget { turns: number | null; minutes: number | null }
export interface WorkerLimits { working_minutes: number; lifetime_minutes: number; token_warning: number }
export interface SessionAllowance {
  budget: SessionBudget | null
  limits: WorkerLimits | null
  turns: number
  working_ms: number
  tokens: number
  token_warned: boolean
  stopped: string | null
  accepted_at: IsoTime
  checked_at: IsoTime
  working: boolean
  token_snapshot: number
  generation: number
}
export interface Session {
  /** Explicit advice role: the read-only mode it started in. */
  advice?: { mode: 'claude_plan' | 'codex_read_only' }
  place?: SessionPlace
  worktree_kept?: boolean
  pending_place?: SessionPlace | null
  allowance?: SessionAllowance
  id: string
  project_id: string
  issue_id: string | null
  runner_id: string
  /** Pinned start identity. Display the actual runtime model from settings.current_model; unknown stays unknown. */
  agent: { vendor: string; model: string }
  /** The runner's agent it runs, as its start named it (`claude`, `claude-api`); absent when not known. */
  runner_agent?: string
  /** The member on whose behalf it runs. */
  started_by: string
  origin: SessionOrigin
  state: SessionState
  /** A completed turn awaits its person; absent while further work or a fresh/resumed idle conversation waits. */
  awaiting_person_since?: IsoTime
  /** A stop its runner has not refused, until the session ends; absent otherwise. */
  stopping?: true
  /** Null until it has ended. */
  ended: SessionEnd | null
  created_at: IsoTime
  /** Latest meaningful activity, recorded by the server. */
  last_active_at: IsoTime
  /**
   * What names it where no issue does: its agent's own title for it, the last it gave, else the first line of the prompt
   * that started it, as the server cleans them: one line of 1 to 120 characters. Null when neither says anything.
   */
  title: string | null
  /** True once a person chose or renamed the title: the agent's later titles leave it alone. */
  title_by_person?: boolean
  /** Null until reported; absent on older sessions. */
  changes?: SessionChanges | null
  /** Last reported runtime observations; absent means unknown, never zero. */
  usage?: SessionUsage
  /** Actual session choices, distinct from discovery and pending requests. */
  settings?: SessionSettings
  /** Runner-owned latest driver observation; requests never assert ownership. */
  driver?: SessionDriver
  /** It ended, and a person may resume it, with its agent's conversation (Poulpe-425); absent from older servers. */
  resumable?: boolean
  /** A person resumed it, and its agent has not opened its conversation again yet. */
  resuming?: boolean
  /** The prompts a person sent while its turn ran, which wait to be sent, in the order they are sent (Poulpe-430); absent when none waits. */
  queued?: QueuedPrompt[]
  /** Poulpe resumed it by itself after its runner restarted, and it has not run since (Poulpe-425). */
  resumed_after_restart?: boolean
  /** A worker's task in one line, as its lead named it (Poulpe-434); absent for any other session. */
  description?: string
  /** Its lead's verdict on a worker's result, which closed it; absent until the lead records one. */
  verdict?: WorkerVerdict
  /** The question a worker waits for its lead to answer: another model's words; absent when none waits. */
  lead_question?: { text: string; at: IsoTime }
}

/** A prompt that waits on a session: shown above the composer until it is sent. */
export interface QueuedPrompt {
  /** What it is sent under, and what `change_queued_prompt` names it by. */
  id: string
  /** Its first 2000 characters. */
  prompt: string
  /** `next`: ahead of the follow-ups; `later`: a follow-up. */
  priority: 'next' | 'later'
  /** The files attached to it, by name, type and size. */
  files: SentFile[]
}

/** What a lead did with its worker's result: took it, took it after fixes of its own, or discarded it. */
export type WorkerVerdictValue = 'taken' | 'taken_after_fixes' | 'discarded'

/** A lead's verdict on its worker's result, recorded with a delegate tool and never from the pages. */
export interface WorkerVerdict {
  value: WorkerVerdictValue
  /** Why, in the lead's words; null when it gave none. */
  reason: string | null
  at: IsoTime
}

export interface SessionDriver {
  conversation_id: string
  generation: number
  revision: number
  transition_id: string
  state: 'acp' | 'releasing' | 'native' | 'returning' | 'uncertain' | 'stopping' | 'ended'
  native_supported: boolean
}

export type CyberAccessProgram = 'standard' | 'daybreakBlue' | 'daybreakRed'
export interface ConfigChoice { id: string; value: string }
export interface SessionSettings {
  access_programs?: Record<string, CyberAccessProgram[]>
  revision: number
  options: AgentOption[]
  modes: AgentChoice[]
  current_model: string | null
  current_mode: string | null
  change?: { job: string; state: 'pending' | 'applying' | 'confirmed' | 'refused' | 'unconfirmed' }
}

/** Observations only: no permission, budget or account-quota policy. */
export interface SessionUsage {
  new_tokens?: number
  /** Last valid context report; later missing/invalid packets do not prove freshness. */
  context?: { used_tokens: number; window_tokens: number }
  /** Latest completed ACP prompt response, never summed across reports. */
  last_prompt?: {
    /** Follows the pinned Claude/Codex adapter contract; no runtime binary attestation. */
    scope: 'prompt' | 'unknown'
    total_tokens: number
    input_tokens: number
    output_tokens: number
    cache_read_tokens?: number
    cache_write_tokens?: number
    reasoning_tokens?: number
  }
  /** Application ACP prompt requests, not hidden vendor/model/tool turns. */
  prompt_requests?: { started: number; finished: number }
  /** Current real-session thought_level (or Vibe thinking) values and labels. */
  effort?: { id: string; name: string; value: string; value_name: string }[]
  /** Last valid explicit cost since this adapter process; not freshness or plan allowance. */
  cost?: { amount: number; currency: string }
}

/** Choices reported by ACP; every selection is revalidated in the real session. */
export interface AgentChoice {
  value: string
  name: string
  description?: string
  group?: string
}

export interface AgentOption {
  id: string
  name: string
  description?: string
  /** The agent's category, including `model`, `mode`, `thought_level`, or `thinking`. */
  category?: string
  /** Scratch discovery state among retained choices; not applied hosted runtime state. */
  current_value?: string
  choices: AgentChoice[]
}

export interface AgentModelDetails {
  access_programs?: CyberAccessProgram[]
  name?: string
  description?: string
  /** Explicitly selectable windows. An absent list means unknown. */
  context_windows?: number[]
  /** Reported capacity alone does not establish that it is selectable. */
  context_window?: number
  /** The effort option reported while this model was the scratch session's. Absent means unknown or none. */
  effort?: AgentOption
}

export interface AgentCatalogue {
  /** Scratch session's current model, not a model admitted or pinned for a hosted start. */
  current_model?: string
  /** Scratch state among retained choices; Claude's later auto negotiation may differ. */
  current_mode?: string
  /** Model-dependent options describe this catalogue's current_model; model_details carry each model's effort. */
  options?: AgentOption[]
  modes?: AgentChoice[]
  /** The mode a session started with none is given (Claude's `auto`, Poulpe-731); never discovery's scratch state. */
  default_mode?: string
  model_details?: Record<string, AgentModelDetails>
}

/** An agent a runner offers. */
export interface RunnerAgent {
  /** Explicit defaults saved on this runner; distinct from discovery state. */
  preferences?: AgentPreferences['defaults']
  /** Its name on the runner: `claude`, `codex`. What `start_session` names. */
  agent: string
  vendor: string
  /** Models admitted by the existing start path; discovery-only catalogue choices do not extend it. */
  models: string[]
  /** Absent until an ACP discovery succeeds. Missing limits mean unknown. */
  catalogue?: AgentCatalogue
  /** The model reported as current; pinned when starting with the default. */
  default_model?: string | null
  /** Model-maker declarations from the runner owner; never inferred from ids. */
  model_vendors?: Record<string, string>
  /** Whether the person has signed it in on that machine. */
  signed_in: boolean | null
}

/** A machine that runs agents for the workspace. The feed brings it as it says hello, goes, or its agents change (Poulpe-367). */
export interface Runner {
  id: string
  /** As its owner named it. */
  name: string
  /** As its last hello named it; null before its first. */
  machine: string | null
  connected: boolean
  /** Connected, since its hello; else since it was last heard. Null when it never was, or connected before the server kept when. */
  since: IsoTime | null
  /** The projects its last hello announced. */
  projects: string[]
  /** Whether it takes work started elsewhere than on its own machine: sessions and prompts among it. */
  accepts_work_from_elsewhere: boolean
  revoked: boolean
  agents: RunnerAgent[]
}

/** Where a runner's job stands: `queued` and `sent` wait for the runner to take it; `accepted` and `started` run; `finished` and `refused` ended. */
export type RunnerJobState = 'queued' | 'sent' | 'accepted' | 'started' | 'finished' | 'refused'

/** A piece of work for one runner (runners/jobs.rs there). The feed brings it when it is made and at each move its runner reports (Poulpe-367). */
export interface RunnerJob {
  id: string
  runner_id: string
  project_id: string
  /** Its work's kind, as the protocol names it: `start_session`, `prompt_session`, `run_check`, `build_candidate`… */
  kind: string
  /** The session its work names, if any. */
  session_id: string | null
  state: RunnerJobState
  created_at: IsoTime
}

/**
 * A one-time code that registers one runner in the workspace, on its maker's
 * behalf, until it expires, ten minutes after it was made. It is answered
 * once: the server keeps only its hash.
 */
export interface RunnerCode {
  /** Twelve characters of Crockford's base32, as the runner's machine asks for it. */
  code: string
  expires_at: IsoTime
}

/**
 * One item of a session's transcript. A runner's is what its agent said, as
 * ACP wrote it: the params of a `session/update` (`{sessionId, update}`), a
 * turn's end (`{stopReason}`) or a turn's error (`{error}`). A sent one is a
 * prompt someone sent: `{prompt, by}`. Poulpe's says what Poulpe did of the
 * session: `{notice, resumed: 'after_restart'}` once it resumed it after its
 * runner restarted (Poulpe-425). The pages read a payload as untrusted
 * (transcript/acp.ts).
 */
export interface TranscriptItem {
  id: string
  at: IsoTime
  source: 'runner' | 'sent' | 'poulpe'
  payload: unknown
}

/** What a sent item holds. */
export interface SentPrompt {
  prompt: string
  by: Actor
  attachments?: SentFile[]
}

export interface TranscriptPage {
  /** Oldest first, after the item asked for. */
  items: TranscriptItem[]
  /** More items follow the last of these. */
  more: boolean
  /**
   * A page read backward (`before`) alone: older items precede the first of
   * these. Its absence says the server read forward from the start, as one
   * before Poulpe-843 does, which ignores `before`.
   */
  older?: boolean
}

// --- The landing queue (Poulpe-341; crates/poulpe-server/src/queue) --------------------------

/** Where a proposal stands: offered, sent back to its session, on main, or taken back. */
export type ProposalState = 'open' | 'returned' | 'landed' | 'withdrawn'

/**
 * Where an open proposal stands: its head is to reach GitHub, its targeted
 * check runs, it waits for its review, for its turn, or it is landing.
 */
export type ProposalStep = 'head' | 'check' | 'review' | 'queued' | 'landing'

/** What a reviewer concludes: only `land` approves. */
export type ReviewOutcome = 'land' | 'land_with_changes' | 'rework'

export interface Finding {
  severity: 'high' | 'medium' | 'low'
  /** Absent when it is in no one file. */
  file?: string
  /** From 1; absent when it is on no one line. */
  line?: number
  what: string
}

export interface ReviewVerdict {
  outcome: ReviewOutcome
  findings: Finding[]
}

/** A review as the queue keeps it: who reviewed, an agent or a person (Poulpe-688), and their verdict. */
export interface ReviewRecord {
  reviewer: Actor
  verdict: ReviewVerdict
  /** A person's words with their verdict, sent to the session when it returned the work; absent for an agent's. */
  note?: string
}

/** Why a subject cannot land: its evidence is missing or failed. */
export type BlockReason =
  | { reason: 'no_full_check' }
  | { reason: 'check_on_other_commit' }
  | { reason: 'check_on_other_tree' }
  | { reason: 'check_on_other_definition' }
  | { reason: 'check_failed'; parts: string[] }
  | { reason: 'no_cross_vendor_review' }
  | { reason: 'review_rejected'; vendor: string }
  /** The policy has a person review, and no person approved it (Poulpe-688). */
  | { reason: 'no_person_review' }
  | { reason: 'person_rejected'; member: string }

/** Why a proposal went back to its session, or was withdrawn. */
export type ProposalEnded =
  | { kind: 'head_not_on_forge' }
  | { kind: 'head_not_pushed'; message: string }
  | { kind: 'check_failed'; receipt: string; parts: string[] }
  | { kind: 'check_not_run'; message: string }
  | { kind: 'reviewed'; outcome: ReviewOutcome }
  | { kind: 'review_not_given' }
  | { kind: 'blocked'; reasons: BlockReason[] }
  | { kind: 'gave_up'; reason: string; rebuilds: number }
  | { kind: 'candidate_refused'; message: string }
  | { kind: 'candidate_not_built'; message: string }
  | { kind: 'replaced'; by: string }
  | { kind: 'repository_changed' }
  /** A person asked for changes (`return_proposal`), with their note, which its session is sent as its next prompt. */
  | { kind: 'changes_asked'; member: string; note: string }
  /** A person, or an agent of its project, withdrew it (`withdraw_proposal`), with a reason: its session is not prompted. */
  | { kind: 'withdrawn'; by: Actor; reason: string }

/** A person who looked at a proposal's head and said it looks good (`look_at_proposal`): it gates nothing. */
export interface LookedAt {
  member: string
  head: string
  at: IsoTime
}

/** The end of a failed check part's output, as its runner sent it: another party's text, shown, never obeyed (poulpe-model, CheckOutput). */
export interface CheckOutput {
  /** The part that failed. */
  part: string
  /** Its last 64 KiB at most, from the start of a line. */
  tail: string
  /** Whether the output began before `tail` does. */
  truncated: boolean
}

/** A proposal's urgency (Poulpe-674, queue/urgency.rs). */
export type Urgency = 'normal' | 'urgent'

/**
 * A proposal's urgency as it stands: who set it last, why and when, and its
 * revision, which a change names (`expected_revision`). Revision 0 was never
 * set: `reason`, `by` and `at` are null then. It orders the landings that may
 * start, and the checks that wait on a runner (Poulpe-680), and nothing else.
 */
export interface ProposalUrgency {
  level: Urgency
  revision: number
  reason: string | null
  by: Actor | null
  at: IsoTime | null
}

/** A session's offer to land its head for its issue (queue/store.rs, ProposalView). */
export interface Proposal {
  id: string
  project_id: string
  /** The first issue it carries: its session's, or the first named. */
  issue_id: string
  /** Every issue it carries, `issue_id` first, in the order named (Poulpe-456). */
  issue_ids: string[]
  session_id: string
  /** The session whose worktree holds its head, when another than its own: its runner pushes, checks and builds it (Poulpe-456). */
  worktree_session_id?: string | null
  /** The commit offered: 40 hexadecimal digits. */
  head: string
  /** The commit its session started from: provenance. */
  base: string
  /**
   * Where its head meets main, read when the head reached the forge: its
   * check, its review and the diff compare the head with it. Null until then.
   */
  main_base: string | null
  /** The vendors of the agents that may have authored its work. */
  authors: string[]
  state: ProposalState
  /** Where it stands while open; null once it has ended. */
  step: ProposalStep | null
  /** Its targeted check's job, once sent. */
  check_job: string | null
  review_requested_at: IsoTime | null
  /** The review session the queue started for it. */
  review_session: string | null
  review_sessions: number
  /** Why its review waits, as its record last said. */
  review_waits: string | null
  review: ReviewRecord | null
  ended: ProposalEnded | null
  landed: string | null
  created_at: IsoTime
  /** Who looked at it, once per member and head, oldest first. */
  looked_at: LookedAt[]
  /**
   * The batch landing whose failure put it back at `queued`: the queue
   * starts it no more on its own, and a person picks it again (`merge_together`).
   */
  held_by: string | null
  /** What the failed part of its check said, when the queue returned it or holds it for that check. */
  check_output: CheckOutput[]
  /** Its urgency: urgent work takes its turn to land first, within a fair allowance. */
  urgency: ProposalUrgency
}

/** The exact thing that lands, which a person's approval binds (poulpe-model, landing.rs). Its sets are sorted. */
export interface LandingSubject {
  /** The commit main would move to. */
  commit: string
  /** That commit's tree. */
  tree: string
  /** The proposals the commit holds. */
  proposals: string[]
  /** The version of the landing policy it is judged by. */
  policy_version: number
  /** The receipts that prove it passed its checks. */
  evidence: string[]
}

/** Why a subject needs a person's approval. */
export type PersonReason =
  | { reason: 'marked_path'; path: string }
  | { reason: 'check_definition_changed'; path: string }
  | { reason: 'checks_changed'; from: string; to: string }

/** A part of a subject that an approval binds. */
export type SubjectPart = 'commit' | 'tree' | 'proposals' | 'policy_version' | 'evidence'

/** An approval refused because it approved another subject. */
export interface StaleApproval {
  approval: string
  /** The parts that differ. */
  differs: SubjectPart[]
}

/** An approval asked for (queue/store.rs, ApprovalView). */
export interface ApprovalAsked {
  subject: LandingSubject
  /** The SHA-256 of the subject's JSON, which the confirmation names: 64 lowercase hexadecimal digits. */
  digest: string
  reasons: PersonReason[]
  /** The approval refused just before, if any. */
  stale: StaleApproval | null
}

/** What a running landing waits for. GitHub (`forge`) is a read of main, its move, or its read back: the view does not say which. */
export type Waiting =
  | { for: 'forge' }
  /** The runner's candidate of `head` on `base`; `build` says whether it is a merge to build, once asked, or the head itself. */
  | { for: 'candidate'; base: string; head: string; build: boolean | null }
  /** The landing check; `base`, the main the candidate was built on, when it covers only the parts the changes affect. */
  | { for: 'full_check'; commit: string; job: string | null; base?: string }
  | ({ for: 'approval' } & ApprovalAsked)

/** How a landing ended. */
export type LandingOutcome =
  | { outcome: 'landed'; commit: string }
  | { outcome: 'policy_changed' }
  | { outcome: 'cancelled'; message: string }
  | { outcome: 'blocked'; reasons: BlockReason[] }
  | { outcome: 'gave_up'; reason: string; rebuilds: number }
  | { outcome: 'check_not_run'; message: string }
  | { outcome: 'candidate_refused'; message: string }
  /** Its candidate could not be built: `conflict` names the proposal that did not merge, and the one before it (`after`), or main (null). */
  | { outcome: 'candidate_not_built'; conflict: Conflict | null }

/** A proposal of a batch that did not merge on what came before it. */
export interface Conflict {
  proposal: string
  /** The proposal before it in the batch; null when it is main. */
  after: string | null
  paths: string[]
}

/**
 * A landing of one proposal, or of several merged together in the order
 * picked (queue/store.rs, LandingView). One runs at a time per project; a
 * batch asked while another runs is `waiting`, and starts next.
 */
export interface Landing {
  id: string
  project_id: string
  /** Its first proposal. */
  proposal_id: string
  /** Its proposals, in order. */
  proposals: string[]
  /** The version of the policy it is judged by. */
  policy_version: number
  state: 'waiting' | 'running' | 'deferred' | 'finished'
  /** What it waits for while it runs; null when it waits for nothing. */
  waiting_for: Waiting | null
  outcome: LandingOutcome | null
  /**
   * Its PM agent's session (Poulpe-406): set once a batch a person picked
   * stopped without landing, and on each retry its PM agent makes, which is
   * a new landing with the same session. Null for any other landing, and
   * absent on a landing kept from before the field.
   */
  pm_session: string | null
}

/** A person's approval of one landing subject. */
export interface Approval {
  id: string
  /** The member who approved. */
  by: string
  subject: LandingSubject
  at: IsoTime
}

// --- Plans (Poulpe-410) ------------------------------------------------------------------
//
// A plan is a project's document that lists its issues, in HTML or Markdown
// (crates/poulpe-server/src/plans, docs/architecture/clients.md, "Plans").
// The snapshot and the feed carry it without its body; `GET /v1/plans/{id}`
// reads the body, its revisions and its comments. A page that shows a body
// reads it again once the feed's `revision` passes the one it shows.

/** A plan's status, as today's board names them. Current plans are In Progress and Planned. */
export type PlanStatus = 'backlog' | 'planned' | 'in_progress' | 'for_review' | 'completed' | 'cancelled'

/** HTML when the body begins with `<!doctype html>`, past a byte-order mark and white space; Markdown otherwise. The body says it: nothing else stores it. */
export type PlanFormat = 'html' | 'markdown'

/** An issue a plan's body lists, by key, with the phase that lists it or null. */
export interface PlanListing {
  key: string
  section: string | null
}

/** A plan as the snapshot and the feed carry it (plans/mod.rs, PlanView): its row without its body. */
export interface PlanRow {
  id: string
  /** Its project's id: set when it is made, never changed. It may list another project's issues. */
  project: string
  title: string
  /** One line, at most 255 characters; empty for none. */
  summary: string
  status: PlanStatus
  format: PlanFormat
  /** Its body's revision: 1 at creation, one more at each change of its body. */
  revision: number
  /** Its place among its project's plans. */
  sort_key: string
  /** Information, not a lock: another's claim is refused while one is held (409 `claimed`). */
  claim: Claim | null
  /** Its reference on today's board, such as `#180`. */
  legacy_ref: string | null
  created_by: Actor
  created_at: IsoTime
  updated_at: IsoTime
  /** Archived plans leave the snapshot; one the feed shows archived leaves. */
  archived_at: IsoTime | null
  /** The issues its body lists, in its order. */
  issues: PlanListing[]
}

/** A revision kept of a plan's body: the text it had at `revision`, kept as `author` replaced it. */
export interface PlanRevisionLine {
  revision: number
  /** `edit`, or today's `conflict`, which an import may carry. */
  reason: string
  author: Actor
  at: IsoTime
}

/** A description an issue replaced (Poulpe-439), without its text: the server keeps one per author per ten minutes. */
export interface IssueRevisionLine {
  /** Its place among the issue's revisions: 1, 2, 3…, in the order they were kept. */
  number: number
  field: 'description'
  /** `edit`, or today's `conflict`, which an import may carry. */
  reason: string
  /** Who replaced the text. */
  author: Actor
  at: IsoTime
  bytes: number
}

/** What `GET /v1/issues/{id}/revisions` answers: the newest 100, newest first. */
export interface IssueRevisions {
  issue: string
  revisions: IssueRevisionLine[]
}

/** What `GET /v1/issues/{id}/revisions/{n}` answers. */
export interface IssueRevision extends IssueRevisionLine {
  issue: string
  body: string
}

/**
 * A comment on a plan (the parallel server work of slice 2): one level of
 * replies, `parent` the comment it answers; edited by its author alone, never
 * deleted. The feed carries it as `plan_comment`; the snapshot does not.
 */
export interface PlanComment {
  id: string
  plan: string
  parent: string | null
  author: Actor
  /**
   * The person who wrote it on today's board, when the import brought it:
   * it shows as theirs, at its original time, marked imported. No one edits
   * it (403 `not_its_author`).
   */
  source_author: string | null
  /** Markdown: 1 to 20,000 characters, not blank. */
  body: string
  created_at: IsoTime
  edited_at: IsoTime | null
}

/** The most characters a plan's comment holds. */
export const PLAN_COMMENT_MAX = 20_000

/** What `GET /v1/plans/{id}` answers: the plan as the feed has it, its body, its revisions oldest first, and its comments oldest first. */
export interface PlanDetail extends PlanRow {
  body: string
  revisions: PlanRevisionLine[]
  /** Absent from a server from before plan comments. */
  comments?: PlanComment[]
}

/** What a search result is. */
export type SearchKind = 'issue' | 'plan' | 'record' | 'plan_comment' | 'repo'

/** One result of `GET /v1/search` (server/search.rs), best first. */
export interface SearchHit {
  path?: string
  source_sha?: string
  kind: SearchKind
  id: string
  /** An issue's key (`Poulpe-012`), or a plan's old reference (`#180`); absent for a plan without one. */
  key?: string
  title: string
  /** Its project's id. */
  project: string
  /** A `record` result: its issue's id (`id` is the entry's; `key` and `title` are the issue's), its place in the record, and its kind. */
  issue?: string
  entry?: number
  entry_kind?: EntryKind
  /** A plan comment: its plan's id (`id` is the comment's). */
  plan?: string
  /** About sixteen words of its text beyond the title, plain: the client marks the terms. */
  snippet: string
  /** `ts_rank_cd`'s: higher is better; 0 for a jump the terms do not match. */
  rank: number
}

/** What a query names: an issue by its key or its number, or a plan by its `#NNN`. Its result comes first. */
export interface SearchJump {
  kind: SearchKind
  id: string
  key: string
}

/** What `GET /v1/search` answers. */
export interface SearchAnswer {
  results: SearchHit[]
  jump?: SearchJump
}

/** Where a search looks: the selected project (by id), every project with `all`; 20 results unless `limit`, at most 100. */
export interface SearchOptions {
  project?: string | null
  all?: boolean
  limit?: number
}

/** One operation of `patch_plan` (plans/patch.rs): each anchor matches the body as it stands exactly once. */
export type PlanPatchOp =
  | { op: 'replace'; old_string: string; new_string: string; replace_all?: boolean }
  | { op: 'insert_before' | 'insert_after'; anchor: string; text: string }
  | { op: 'prepend' | 'append'; text: string }
  | { op: 'replace_range'; from: string; to: string; new_string: string }

/** A new plan of `project`, in Backlog unless it names its status, first among its project's plans; without a body, today's HTML skeleton. */
export interface CreatePlanArgs {
  id?: string
  project: string
  title: string
  summary?: string
  status?: PlanStatus
  body?: string
}

/** Last write wins on every field. */
export interface UpdatePlanArgs {
  id: string
  title?: string
  summary?: string
  status?: PlanStatus
  sort_key?: string
  archived?: boolean
}

/** The whole body, with the base it was edited from: merged with the changes made since, or refused 409 `text_conflict`, field `body`. */
export interface SetPlanBodyArgs {
  id: string
  body: string
  base?: string
}

export interface PatchPlanArgs {
  id: string
  /** 1 to 50 operations, all or nothing. */
  patch: PlanPatchOp[]
}

/** Lists the issue under the first phase whose heading begins with `section`, or a new phase of that name; `added: false` when the plan lists it already. */
export interface ListInPlanArgs {
  plan: string
  /** The issue's id. */
  issue: string
  section?: string
}

/** `unlist_from_plan`: the items that list the issue go; mentions of it stay. */
export interface UnlistFromPlanArgs {
  plan: string
  /** The issue's id. */
  issue: string
}

/** `move_in_plan`: the item that lists the issue moves under the phase `section` names, or a new one. */
export interface MoveInPlanArgs {
  plan: string
  /** The issue's id. */
  issue: string
  section: string
}

export interface PlanIdArgs {
  id: string
}

/**
 * A comment on a plan, or a reply to one of its comments (`parent`): one
 * level of replies (400 `reply_to_reply`), to a comment of the same plan
 * (400 `parent_elsewhere`); a blank body or one over 20,000 characters is 400.
 */
export interface AddPlanCommentArgs {
  id?: string
  plan: string
  body: string
  parent?: string
}

/** Its author alone edits it: 403 `not_its_author` otherwise. */
export interface EditPlanCommentArgs {
  id: string
  body: string
}

// --- Runs --------------------------------------------------------------------------------

export type RunKind = 'session' | 'check' | 'verify' | 'investigate' | 'review'
export type RunStatus = 'running' | 'waiting' | 'passed' | 'failed' | 'cancelled'

/** One item of a run's evidence. */
export interface RunEvidence {
  label: string
  value?: string
  /** `http(s)://…` or a board path. */
  url?: string
}

/**
 * A standalone check, verification, investigation or review that an agent
 * records with `start_run`, keeps alive with `update_run` and ends with
 * `finish_run`: on an issue, or on a project alone. Read only: the pages write
 * no run. `updated_at` is its last heartbeat.
 */
export interface Run {
  id: string
  project: string
  issue: string | null
  plan: string | null
  parent: string | null
  kind: RunKind
  title: string
  status: RunStatus
  actor: Actor
  session: string | null
  sha: string | null
  /** Markdown: where it stands; empty until given. */
  summary: string
  evidence: RunEvidence[]
  cost_usd: number | null
  /** The queue's whole check of main. Clients never write this. */
  main_check?: { repository: string; branch: string; job: string; output: CheckOutput[] }
  started_at: IsoTime
  updated_at: IsoTime
  ended_at: IsoTime | null
}

// --- The feed and the snapshot ---------------------------------------------------------

/** A persistent board attachment; bytes are fetched separately within the workspace. */
export interface BoardAttachment {
  /** Content address retained by an import from today's board. */
  old_sha256?: string
  id: string
  issue: string | null
  plan: string | null
  entry: string | null
  comment: string | null
  title: string
  url: string | null
  mime_type: string | null
  size: number
  author: Actor
  created_at: string
}

export interface AttachFileArgs {
  issue?: string
  plan?: string
  entry?: string
  comment?: string
  title?: string
  file?: WireAttachment
  url?: string
}

/** An entity under its kind's name, as a change and a mutation's result carry it. */
export type Entity =
  | { entity: 'attachment'; data: BoardAttachment }
  | { entity: 'project'; data: Project }
  | { entity: 'label'; data: Label }
  | { entity: 'issue'; data: Issue }
  | { entity: 'record_entry'; data: RecordEntry }
  | { entity: 'question'; data: Question }
  | { entity: 'session'; data: Session }
  | { entity: 'proposal'; data: Proposal }
  | { entity: 'landing'; data: Landing }
  | { entity: 'plan'; data: PlanRow }
  | { entity: 'plan_comment'; data: PlanComment }
  | { entity: 'run'; data: Run }
  | { entity: 'runner'; data: Runner }
  | { entity: 'job'; data: RunnerJob }

/** One entry of the workspace's change feed: the entity as the change left it. */
export type Change = Entity & {
  seq: number
  /** The entity's id. */
  id: string
  /** The mutation that made it. */
  mutation_id: string
  actor: Actor
}

export interface ChangesPage {
  /** In seq order, after `since`. */
  changes: Change[]
  /** The `since` of the next request: the last change's seq, or `since` when nothing is new. */
  cursor: number
}

export interface Snapshot {
  attachments?: BoardAttachment[]
  /** The seq these rows reflect: the cursor to follow the feed from. */
  seq: number
  /**
   * The feed's epoch, given back with the cursor: a feed of another epoch, a
   * restored server's, answers 410 or 4410 (Poulpe-792). A server from before it sends none.
   */
  epoch?: string
  projects: Project[]
  /** The projects' labels that are not deleted, oldest first. A label the feed shows deleted leaves. */
  labels: Label[]
  issues: Issue[]
  /** Every entry of every issue's record, issue by issue, in order. */
  record_entries: RecordEntry[]
  /** The questions in play: open, or answered and not confirmed delivered. A question the feed shows otherwise leaves. */
  questions: Question[]
  /** The workspace's agents' sessions. */
  sessions: Session[]
  /** The open proposals, oldest first. A proposal the feed shows ended leaves. */
  proposals: Proposal[]
  /**
   * The proposals returned to their session that are the newest of their issue, oldest first: what the board's Stuck reads
   * (Poulpe-441). One the feed shows open again, ended otherwise, or followed by a newer proposal of its issue leaves.
   * Absent from a server from before it.
   */
  returned?: Proposal[]
  /**
   * The running and waiting landings, and each finished one that holds an
   * open proposal (`held_by`), which says why. A landing the feed shows
   * finished leaves, unless it holds one.
   */
  landings: Landing[]
  /** The plans not archived, oldest first, without their bodies; absent from a server from before plans. */
  plans?: PlanRow[]
  /** The active runs and the newest others, oldest first; absent from a server from before runs. */
  runs?: Run[]
  /** Every runner, revoked ones included, oldest first; absent from a server from before Poulpe-367. */
  runners?: Runner[]
  /** The jobs with no outcome yet, oldest first. A job the feed shows finished or refused leaves. */
  jobs?: RunnerJob[]
}

// --- Mutations ---------------------------------------------------------------------------

/** A project's key is 1 to 12 ASCII letters and digits, starting with a letter, and unique in the workspace whatever its case (poulpe-model, key.rs). */
export interface CreateProjectArgs {
  /** The client may choose it, to refer to the project before the server confirms it. */
  id?: string
  key: string
  name: string
}

export interface CreateIssueArgs {
  /** The client may choose it, to refer to the issue before the server confirms it. */
  id?: string
  project_id: string
  title: string
  state?: StateSlug
  priority?: Priority
  description?: string
  summary?: string
}

// An edit of a part (`update_issue`'s description, `set_plan`,
// `set_outcome`) may name its base, the text it started from (the server's
// board/issues.rs, and poulpe-model's merge.rs). With no base, with the
// current text as its base, or sending the current text, the text sent is
// stored: last write wins. Otherwise the server merges it, line by line,
// with the changes made since its base: the result holds the merged text,
// and says `merged: true`. Edits of the same or neighbouring lines cannot
// merge, nor texts of more than 10,000 lines: the op is refused, 409
// `text_conflict` (TextConflictBody), and nothing changes. Agents through
// MCP send no base.

/** The fields it names change; the plan, the outcome and the state have ops of their own. The description may name its base, as a part's edit does (above). */
export interface UpdateIssueArgs {
  id: string
  title?: string
  priority?: Priority
  description?: string
  /** One line of at most 200 characters; empty clears it. */
  summary?: string
  /** The description the edit started from; only with `description` (400 otherwise). */
  description_base?: string
}

/**
 * To another state, or into a column of the project's own (a state, a column,
 * or both when they agree: 400 otherwise), as the model's rules allow the
 * actor for the column's state, and to a place in the column: `sort_key` when
 * given (400 when it is no key, sortKey.ts); otherwise the top of the new
 * column when the column changes, as today, and where it was when it does not.
 */
export interface MoveIssueArgs {
  id: string
  state?: StateSlug
  column?: string
  sort_key?: string
}

/** A person's: an agent's is refused, 403 `label_not_by_person`. A name another label of the project has, whatever its case, is refused, 409. */
export interface CreateLabelArgs {
  /** The client may choose it, to refer to the label before the server confirms it. */
  id?: string
  project_id: string
  name: string
  colour: string
}

/** Renames the label, gives it another colour, or both: one at least. A person's; a deleted label is refused, 409 `label_deleted`. */
export interface UpdateLabelArgs {
  id: string
  name?: string
  colour?: string
}

/** A person's, once: the label leaves every issue, and the feed carries each after the label. */
export interface DeleteLabelArgs {
  id: string
}

/**
 * Puts a label of the issue's project on it (`add_label`), or takes it off
 * (`remove_label`): again changes nothing. A person changes any issue's; an
 * agent, an issue's of its session's project (403 `not_its_project`). Another
 * project's label is refused, 400, and a deleted one, 409 `label_deleted`.
 */
export interface IssueLabelArgs {
  /** The issue. */
  id: string
  label_id: string
}

/**
 * Relates the issue to another, as `kind` says from its side
 * (`relate_issues`), or undoes it (`unrelate_issues`): either changes
 * nothing when there is nothing to change. Both issues may be of any
 * project of the workspace, but not the same issue (400). A person relates
 * any two; an agent, two of which one at least is of its session's project
 * (403 `relation_not_its_project`). Each issue's record says what changed.
 */
export interface RelateIssuesArgs {
  issue: string
  kind: RelationKind
  other: string
}

/**
 * Makes the issue a sub-issue of `parent`, an issue of its own project, or
 * none when `parent` is null (`set_parent`): setting the parent it has
 * changes nothing. A parent of another project, one that makes a loop, and
 * a chain of more than 8 levels are refused, 409 `parent_other_project`,
 * `parent_cycle` and `parent_too_deep`. A person sets any issue's parent;
 * an agent, an issue's of its session's project (403 `parent_not_its_project`).
 * The records of the issue and of the parents it leaves and joins say what
 * changed.
 */
export interface SetParentArgs {
  issue: string
  parent: string | null
}

/**
 * Claims the issue for the actor (`claim_issue`), renewing its own claim,
 * or releases its claim, whoever holds it (`release_issue`). Another's
 * claim is refused, 409 `claimed`; an agent claims and releases only an issue
 * of its session's project (403 `not_its_project`).
 */
export interface ClaimArgs {
  /** The issue. */
  id: string
}

/** A different text drops the plan's approval; the same text keeps it. It may name its base, as a part's edit does (above UpdateIssueArgs). */
export interface SetPlanArgs {
  id: string
  plan: string
  /** The plan's text the edit started from. */
  base?: string
}

/** It may name its base, as a part's edit does (above UpdateIssueArgs). */
export interface SetOutcomeArgs {
  id: string
  outcome: string
  /** The outcome the edit started from. */
  base?: string
}

/** Its author is the token's actor, and its time the server's. */
export interface AddRecordArgs {
  id?: string
  issue_id: string
  kind: EntryKind
  body: string
  refs?: Reference[]
}

/** The answerer is the token's actor; an answer to another version than the question's is refused. */
export interface AnswerArgs {
  question_id: string
  version: number
  /** One reply per prompt, in the prompts' order. */
  replies: Reply[]
}

export interface CancelQuestionArgs {
  question_id: string
}

/** An agent's session asks; the pages never do. */
export interface AskArgs {
  /** Immediate browser push, even at the Mac. Default false. */
  urgent?: boolean
  /** Whether the agent waits for the answer. Default true. */
  blocking?: boolean
  /** When the agent stops waiting, in minutes from now (1 to 10,080), with `instead`; only when it waits. */
  expires_in_minutes?: number
  /** What the agent goes on with then, 1,000 characters at most. */
  instead?: string
  id?: string
  issue_id?: string
  kind: Question['kind']
  target: Target
  prompts: Prompt[]
}

/**
 * The session that asked revises its open question in one version: what is
 * left out stays as it was, and one change at least is given (400
 * `nothing_revised`). The terms follow `ask`'s rules; `clear_expiry` removes
 * the expiry, never with a new one (400 `expiry_cleared_and_set`), and a
 * question no longer waited on keeps no expiry unless the same revision
 * clears it (400 `expiry_without_waiting`).
 */
export interface ReviseQuestionArgs {
  question_id: string
  prompts?: Prompt[]
  urgent?: boolean
  blocking?: boolean
  /** A new expiry, in minutes from now (1 to 10,080), with `instead`. */
  expires_in_minutes?: number
  instead?: string
  clear_expiry?: boolean
}

/**
 * Starts an agent on a runner, for a project and perhaps an issue, with a
 * first prompt. A person starts it (403 `start_not_by_person`), and the
 * runner's last hello decides, 409 with its reason: `revoked`,
 * `project_not_served`, `no_work_from_elsewhere` (the pages are always
 * elsewhere than the runner's machine), `agent_not_offered`,
 * `agent_signed_out` or `model_not_offered`. Another workspace's runner,
 * project or issue is not found (404).
 */
export interface StartSessionArgs {
  place?: SessionPlace
  budget?: SessionBudget
  /** The client chooses it, to send the same start again without starting two. */
  session_id: string
  project_id: string
  issue_id?: string
  runner_id: string
  /** The agent, by its name on the runner. */
  agent: string
  /** One of the models the agent offers; absent for the agent's default. */
  model?: string
  mode?: string
  effort?: ConfigChoice
  cyber_access_program?: CyberAccessProgram
  prompt: string
  /** The commit to start from; absent for the runner's choice. */
  base?: string
  /** The person's own title for it, cleaned as the server cleans an agent's; absent for the prompt's first line. */
  title?: string
}

/**
 * A prompt, an interrupt and a stop are jobs for the session's runner,
 * sent by a person alone (403 `steer_not_by_person`), refused (409) once
 * the session has ended (`session_ended`), or by the
 * runner's rules, as a start is (`revoked`, `no_work_from_elsewhere`…). Each
 * answers the session: a prompt moves it to running, on the feed, unless it
 * runs already; an interrupt or a stop changes nothing until the runner
 * reports what became of it.
 */
export interface PromptSessionArgs {
  session_id: string
  /** The words; they may be empty when files are attached. */
  prompt: string
  /**
   * Files attached to the prompt (Poulpe-478): at most ten, 20 MB each
   * and 200 MB together. The bytes decide the type. Files beyond the limits
   * are refused (400), saying which file and why.
   */
  attachments?: WireAttachment[]
  /**
   * Where it goes while the session works (Poulpe-430): `later`, the default,
   * waits behind the prompts that wait; `next`, ahead of them; `now` does not
   * wait but goes into the turn that runs. A prompt sent while the session
   * does not work goes at once, whatever it says. The answer names the prompt
   * that waits as `queued_prompt`.
   */
  priority?: 'now' | 'next' | 'later'
}

/**
 * What a person does with a prompt that waits (Poulpe-430): send it now,
 * into the turn that runs; take it back, whole, in the answer's `unsent`;
 * move it ahead of the follow-ups (`next`) or behind them (`later`). One
 * sent, or taken back, waits no more (409 `not_waiting`).
 */
export interface ChangeQueuedPromptArgs {
  session_id: string
  prompt_id: string
  change: 'send_now' | 'unsend' | 'next' | 'later'
}

/** A prompt taken back: its words and its files, to edit and send again. */
export interface UnsentPrompt {
  prompt: string
  attachments: WireAttachment[]
}

/** A file attached to a prompt, its bytes as base64. */
export interface WireAttachment {
  name: string
  mime_type: string
  data: string
}

/** A sent file's metadata and authenticated reference; bytes stay out of the transcript. */
export interface SentFile {
  /** Retained server copy; absent on prompts sent before Poulpe-478. */
  id?: string
  name: string
  mime_type: string
  bytes: number
}

export interface SessionArgs {
  session_id: string
}

/**
 * A person approves exactly a landing's subject, with a fresh confirmation
 * of `approve_landing` and the subject's digest. It answers whether the
 * landing accepted it: one that approves another subject than the one asked
 * is refused by the landing, which asks again (`accepted: false`, and the
 * landing, waiting for the new subject's approval). Only a person approves
 * (403 `approval_not_by_person`); a landing that waits for no approval
 * answers 409 `no_approval_asked`, and one with no live confirmation of
 * exactly the subject, 403 `unconfirmed`.
 */
export interface ApproveLandingArgs {
  landing_id: string
  subject: LandingSubject
}

/**
 * A person says a proposal looks good: it records that this member looked at
 * its head, once per member and head (a repeat changes nothing), and answers
 * the proposal. It gates nothing. Only a person looks (403
 * `look_not_by_person`).
 */
export interface ProposalArgs {
  proposal: string
}

/**
 * A person asks for changes: the open proposal ends as returned (`ended`
 * `changes_asked`), its issue's record says who asked and the note, as
 * Poulpe's landing entry "Returned to its session: <name> asked for changes:
 * <note>", and its session is sent the note as its next prompt. The note is
 * trimmed, then 1 to 4,000 characters, with no control character but a line
 * break (400 `bad_request`). Only a person returns one (403
 * `return_not_by_person`); an ended proposal is 409 `proposal_ended`, and one
 * that is landing, 409 `landing`.
 */
export interface ReturnProposalArgs {
  proposal: string
  note: string
}

/**
 * A person withdraws an open proposal that will never land: it ends as
 * withdrawn (`ended` `withdrawn`), a review under way stops, and its
 * session is not prompted. Its issue's record says who and why, as
 * Poulpe's landing entry "Withdrawn by <name>: <reason>". The reason is
 * trimmed, then 1 to 4,000 characters, with no control character but a
 * line break (400 `bad_request`). An agent withdraws only a proposal of its
 * own session's project (403 `withdraw_other_project`); an ended proposal
 * is 409 `proposal_ended`, and one that is landing, 409 `landing`.
 */
export interface WithdrawProposalArgs {
  proposal: string
  reason: string
}

/** The longest note `return_proposal`, and reason `withdraw_proposal`, takes, in characters. */
export const RETURN_NOTE_MAX = 4_000

/**
 * A person's verdict on a proposal that waits for one, while its project's
 * policy has a person review (Poulpe-688): for the exact head they read
 * (409 `other_head` once it changed), with a note for its session unless it
 * lands. 409 `agent_reviews` while an agent reviews, `verdict_recorded` once
 * given, `no_review_asked` when it waits for no review.
 */
export interface ReviewVerdictArgs {
  proposal_id: string
  outcome: ReviewOutcome
  head: string
  note?: string
}

/**
 * A person merges ready proposals of one project together, in the order
 * picked: 2 to 10 of them, or one alone that a failed batch holds
 * (`held_by`). It answers the landing, running, or waiting while another
 * runs. Only a person merges (403 `merge_not_by_person`); too few, too many
 * or a repeat is 400; one not open at `queued` with a `land` review is 409
 * `not_ready`, and one of another project, 409 `other_project`.
 */
export interface MergeTogetherArgs {
  proposals: string[]
}

/** The most proposals `merge_together` takes. */
export const MERGE_MAX = 10

/**
 * Marks an open proposal urgent, or normal again, with a reason, from the
 * urgency's revision read (`expected_revision`). A person may, as may the
 * session that proposed it, the session whose worktree holds its head, or a
 * live lead of either. The reason is trimmed, then 1 to 500 characters (400
 * `bad_request`). A change since the revision read is 409 `urgency_changed`;
 * an ended proposal, 409 `proposal_ended`. It answers the proposal.
 */
export interface SetProposalUrgencyArgs {
  proposal: string
  urgency: Urgency
  reason: string
  expected_revision: number
}

/** The longest reason `set_proposal_urgency` takes, in characters. */
export const URGENCY_REASON_MAX = 500

export type Op =
  | { op: 'attach_file'; args: AttachFileArgs }
  | { op: 'create_project'; args: CreateProjectArgs }
  | { op: 'set_project_repository'; args: { project_id: string; owner: string; name: string; branch?: string } }
  | { op: 'set_pm_agent'; args: SetPmAgentArgs }
  | { op: 'set_project_policy'; args: { project_id: string; policy: LandingPolicy } }
  | { op: 'set_release_map'; args: { project_id: string; map: ReleaseMap | null } }
  | { op: 'set_worker_limits'; args: { project_id: string; expected: WorkerLimits | null; limits: WorkerLimits | null } }
  | { op: 'set_project_columns'; args: SetProjectColumnsArgs }
  | { op: 'create_issue'; args: CreateIssueArgs }
  | { op: 'update_issue'; args: UpdateIssueArgs }
  | { op: 'move_issue'; args: MoveIssueArgs }
  | { op: 'claim_issue'; args: ClaimArgs }
  | { op: 'release_issue'; args: ClaimArgs }
  | { op: 'create_label'; args: CreateLabelArgs }
  | { op: 'update_label'; args: UpdateLabelArgs }
  | { op: 'delete_label'; args: DeleteLabelArgs }
  | { op: 'add_label'; args: IssueLabelArgs }
  | { op: 'remove_label'; args: IssueLabelArgs }
  | { op: 'relate_issues'; args: RelateIssuesArgs }
  | { op: 'unrelate_issues'; args: RelateIssuesArgs }
  | { op: 'set_parent'; args: SetParentArgs }
  | { op: 'set_plan'; args: SetPlanArgs }
  | { op: 'set_outcome'; args: SetOutcomeArgs }
  | { op: 'add_record'; args: AddRecordArgs }
  | { op: 'answer'; args: AnswerArgs }
  | { op: 'cancel_question'; args: CancelQuestionArgs }
  | { op: 'ask'; args: AskArgs }
  | { op: 'revise_question'; args: ReviseQuestionArgs }
  | { op: 'start_session'; args: StartSessionArgs }
  | { op: 'change_session_folder'; args: SessionArgs & {place: SessionPlace; approve_plan?: boolean} }
  | { op: 'set_session_budget'; args: { session_id: string; budget: SessionBudget } }
  | { op: 'delegate_set_limits'; args: { session_id: string; limits: WorkerLimits } }
  | { op: 'rename_session'; args: { session_id: string; title: string } }
  | { op: 'set_session_settings'; args: { session_id: string; expected_revision: number; model?: string; mode?: string; effort?: ConfigChoice; cyber_access_program?: CyberAccessProgram | null } }
  | { op: 'handoff_session' | 'return_session'; args: { session_id: string; expected_generation: number; expected_revision: number } }
  | { op: 'prompt_session'; args: PromptSessionArgs }
  | { op: 'change_queued_prompt'; args: ChangeQueuedPromptArgs }
  | { op: 'interrupt_session'; args: SessionArgs }
  | { op: 'stop_subagent'; args: SessionArgs & { subagent_id: string } }
  | { op: 'stop_session'; args: SessionArgs }
  | { op: 'resume_session'; args: SessionArgs }
  | { op: 'refresh_changes'; args: SessionArgs }
  | { op: 'approve_landing'; args: ApproveLandingArgs }
  | { op: 'look_at_proposal'; args: ProposalArgs }
  | { op: 'return_proposal'; args: ReturnProposalArgs }
  | { op: 'review_verdict'; args: ReviewVerdictArgs }
  | { op: 'withdraw_proposal'; args: WithdrawProposalArgs }
  | { op: 'merge_together'; args: MergeTogetherArgs }
  | { op: 'set_proposal_urgency'; args: SetProposalUrgencyArgs }
  | { op: 'create_plan'; args: CreatePlanArgs }
  | { op: 'update_plan'; args: UpdatePlanArgs }
  | { op: 'set_plan_body'; args: SetPlanBodyArgs }
  | { op: 'patch_plan'; args: PatchPlanArgs }
  | { op: 'list_in_plan'; args: ListInPlanArgs }
  | { op: 'unlist_from_plan'; args: UnlistFromPlanArgs }
  | { op: 'move_in_plan'; args: MoveInPlanArgs }
  | { op: 'claim_plan'; args: PlanIdArgs }
  | { op: 'release_plan'; args: PlanIdArgs }
  | { op: 'add_plan_comment'; args: AddPlanCommentArgs }
  | { op: 'edit_plan_comment'; args: EditPlanCommentArgs }

/** A change as the client sends it. A mutation id the server has applied returns the recorded answer, and applies nothing again. */
export type Mutation = Op & { mutation_id: string }

/** The ops on an agent's session: each answers the session. */
export type SessionOp = Extract<Op, { op: 'start_session' | 'change_session_folder' | 'set_session_budget' | 'set_session_settings' | 'handoff_session' | 'return_session' | 'prompt_session' | 'change_queued_prompt' | 'interrupt_session' | 'stop_subagent' | 'stop_session' | 'resume_session' | 'refresh_changes' }>

/** What a mutation answers, the first time and every time after. */
export interface MutationResult {
  mutation_id: string
  /** The feed's seq once the mutation applied: its last change's. */
  seq: number
  /** The entity it created or changed, under its kind's name. */
  result:
    | { attachment: BoardAttachment }
    | { project: Project }
    | { label: Label }
    | {
        issue: Issue
        /** True when a part's edit merged with the changes made since its base; absent otherwise. */
        merged?: boolean
      }
    | { record_entry: RecordEntry }
    | { question: Question }
    | {
        session: Session
        job_id?: string
        /** `prompt_session`: the id of the prompt when it waits on the session (Poulpe-430). */
        queued_prompt?: string
        /** `change_queued_prompt` with `unsend`: the prompt taken back. */
        unsent?: UnsentPrompt
        /** `interrupt_session`: how many prompts that waited were dropped. */
        dropped?: number
      }
    | { proposal: Proposal }
    | ApprovedLanding
    | { landing: Landing }
    | {
        plan: PlanRow
        /** `set_plan_body`: true when the edit merged with the changes made since its base. */
        merged?: boolean
        /** `list_in_plan`: false when the plan listed the issue already, and nothing changed. */
        added?: boolean
        /** `unlist_from_plan`: false when the plan did not list the issue, and nothing changed. */
        removed?: boolean
        /** `move_in_plan`: false when the body stayed as it was. */
        moved?: boolean
      }
    | { plan_comment: PlanComment }
}

/** What `approve_landing` answers: the approval, whether the landing accepted it, and the landing as it now stands. */
export interface ApprovedLanding {
  approval: Approval
  accepted: boolean
  landing: Landing
}

/** The body of every refusal. */
export interface ErrorBody {
  error: string
  message: string
  /** The rule's reason, when a rule refused: `stale`, `not_the_target`, `landed`… */
  reason?: string
}

/** The reason of a part's edit that names its base and cannot merge with the changes made since (above UpdateIssueArgs): 409, and nothing changed. */
export const TEXT_CONFLICT = 'text_conflict'

/** The body of a `text_conflict` refusal: the part, its text as it now stands, and the text the edit sent. */
export interface TextConflictBody extends ErrorBody {
  reason: typeof TEXT_CONFLICT
  /** The part, or a plan's `body`. */
  field: Part | 'body'
  current: string
  incoming: string
}

/** The code of the 410 that says the feed cannot continue from the cursor. */
export const SNAPSHOT_NEEDED = 'snapshot_needed'

/** The reason of an answer to an older version of its question. */
export const STALE = 'stale'

/** The code of the 401 that refuses a request naming another member than its session's or token's (`Api`'s `as`). */
export const IDENTITY_CHANGED = 'identity_changed'

/** The reason of a refused passkey ceremony: unknown, used or expired, or an answer that fails. */
export const PASSKEY_REFUSED = 'passkey_refused'

/** The reason of an action that needs a fresh confirmation of exactly it, when none is live. */
export const UNCONFIRMED = 'unconfirmed'

/** The reason of a removal of the person's last passkey, when they have no email address to sign in with instead. */
export const LAST_PASSKEY = 'last_passkey'

/** The reason of a sign-up the server's setting refuses: sign-up is closed. */
export const SIGNUP_CLOSED = 'signup_closed'

/** The reason of a refused email link, or of a request for one that breaks a rule of form: malformed, unknown, spent or expired. */
export const EMAIL_LINK_REFUSED = 'email_link_refused'

// --- People ------------------------------------------------------------------------------

export interface Named {
  id: string
  name: string
}

/** Who the requests act as, and where: `GET /v1/me`, and what signing up or in answers. */
export interface Me {
  actor: Actor
  /** The person's member of the workspace; null for an agent or Poulpe. */
  member: Named | null
  workspace: Named
  /** The workspace's default for new plans; HTML on older servers. */
  plan_format?: PlanFormat
  /** The person's verified email address, if they have one; absent from an older server. */
  address?: string | null
  /**
   * Whether the person confirms their actions with a passkey: on unless they
   * turned it off (people/confirmations.rs, Poulpe-686); null for an agent,
   * absent from an older server, which always asks.
   */
  confirms_actions?: boolean | null
}

export interface SignUpArgs {
  /** The person's name, as others see it: 1 to 100 characters. */
  name: string
  /** Their first workspace's name, as long. */
  workspace: string
}

/** Whether sign-up is closed now; and whether it is open to listed addresses alone, by email, a passkey proving none. */
export interface SignUpStatus {
  closed: boolean
  email_only?: boolean
}

/** A ceremony the server started: its id, which only this answer gives, and the options for the browser. */
export interface Started<Options> {
  ceremony: string
  options: Options
}

/** What `navigator.credentials.create()` takes, as JSON: webauthn-rs's `CreationChallengeResponse`. */
export interface CreationOptions {
  publicKey: PublicKeyCredentialCreationOptionsJSON
}

/** What `navigator.credentials.get()` takes, as JSON: webauthn-rs's `RequestChallengeResponse`. */
export interface RequestOptions {
  publicKey: PublicKeyCredentialRequestOptionsJSON
  /** The server asks for `conditional`; the pages sign in from a button instead (api/passkeys.ts). */
  mediation?: CredentialMediationRequirement
}

/** What a confirmation approves: a kind of action, and the SHA-256 of exactly what it approves, in lowercase hexadecimal. */
export interface ConfirmAction {
  kind: string
  digest: string
}

/** What `confirm/finish` answers: the confirmation is live until `expires_at`, for one use. */
export interface Confirmed {
  action: ConfirmAction
  expires_at: IsoTime
}

/**
 * What `email/start` takes: the address, the challenge of the verifier the
 * page keeps (the SHA-256 of its ASCII bytes, in base64url without padding),
 * and whether the link signs in or up; signing up names the person and their
 * first workspace, as a passkey's sign-up does.
 */
export interface EmailStartArgs {
  address: string
  challenge: string
  purpose: 'signin' | 'signup'
  name?: string
  workspace?: string
  /** The Mac app it is asked from, by the scheme it answers to: its mail opens that app. Left out in a browser. */
  app?: EmailApp
}

/** The Mac apps an email link can open: the official app, and the development app (Poulpe-816). */
export type EmailApp = 'poulpe' | 'poulpe-next-dev'

/** What `email/add/start` takes: the address to add to the signed-in person, and the challenge. */
export interface EmailAddArgs {
  address: string
  challenge: string
  /** As `EmailStartArgs.app`. */
  app?: EmailApp
}

/** A link on its way, or not: the server answers the same whatever the address's account. */
export interface EmailStarted {
  /** Opaque: the link names it, and finishing it does. */
  attempt: string
  expires_at: IsoTime
}

/** What `email/finish` takes: the link's attempt and token, and the verifier its challenge was made of. */
export interface EmailFinishArgs {
  attempt: string
  token: string
  verifier: string
}

/** One of the signed-in person's passkeys: the row's id (never the credential's), the name they gave it, and when it was added and last signed in or confirmed. */
export interface PasskeyEntry {
  id: string
  name: string | null
  created_at: IsoTime
  last_used_at: IsoTime | null
}

/** The signed-in person's passkeys: how many, and each one, oldest first. */
export interface PasskeyList {
  count: number
  passkeys: PasskeyEntry[]
}

/** A ceremony's answer: the browser's credential, as `PublicKeyCredential.toJSON()` writes it. */
export interface Finish<Answer> {
  ceremony: string
  credential: Answer
}

export interface DigestLine {
  id: string
  key: string | null
  title: string
  detail: string | null
  actor: Actor | null
  at: string
}
export interface DigestGroup { items: DigestLine[]; more: boolean }
export interface WorkDigest {
  since: string
  until: string
  landed: DigestGroup
  failed: DigestGroup
  decided: DigestGroup
  filed: DigestGroup
  investigated: DigestGroup
  runs: number
  cost_usd: number | null
}

/** Named credentials act as their issuing member, within the chosen operations. */
export interface ProgramToken {
  id: string
  name: string
  scopes: string[]
  created_at: string
  expires_at: string
  revoked_at: string | null
}
export interface ProgramTokens {
  programs: ProgramToken[]
  scopes: string[]
  mcp_url: string
}
export interface NewProgramToken { id: string; secret: string }
export interface ProgramTokenArgs { name: string; scopes: string[] }

/**
 * What the pages need of the server; the HTTP client and the mock both give it.
 *
 * `as` is the member the pages act as, once they know who is signed in: the
 * request names it (`Poulpe-As`), and the server refuses it, 401
 * `identity_changed`, when its session or token acts as anyone else, as when
 * another tab signed in as someone else under this one. Nothing it asked
 * for is read or changed then. `me` names no one: it is how the pages learn
 * who is signed in.
 */
export interface PushDevice { id: string; label: string | null; created_at: string; last_ok_at: string | null; failures: number }
/**
 * `native`: the iPhone app's notices (Poulpe-627). `off` without an APNs key;
 * `misconfigured` while APNs refuses the server's key or topic.
 */
export interface PushStatus { supported: boolean; key?: string; devices?: PushDevice[]; native?: 'on' | 'off' | 'misconfigured' }
/** The iPhone app's APNs token, as its shell hands it to the page (Poulpe-627). */
export interface PhoneRegistration { token: string; environment: 'sandbox' | 'production'; app: string }
export interface PhoneDevice { id: string; environment: 'sandbox' | 'production'; created_at: string; last_ok_at: string | null; failures: number }
export interface PushRegistration { endpoint: string; keys: { p256dh: string; auth: string }; label: string }
export interface PushTestResponse { results: { device: string; ok: boolean; error?: string }[] }

/**
 * One of the web app's failures, as `POST /v1/web-reports` takes it
 * (Poulpe-706). A failed request is told by its method, path (no query),
 * status (0 when the server did not answer) and `x-request-id`; an uncaught
 * error by its name, message and stack. A message is free text and can quote
 * what a person typed: the server caps and redacts it again.
 */
export interface WebReport {
  kind: 'error' | 'rejection' | 'request'
  /** The page's path, without its query or fragment. */
  page: string
  name?: string
  message?: string
  stack?: string
  method?: string
  path?: string
  status?: number
  request_id?: string
}

export interface Api {
  programTokens(as?: string | null): Promise<ProgramTokens>
  createProgramToken(args: ProgramTokenArgs, as?: string | null): Promise<NewProgramToken>
  revokeProgramToken(id: string, as?: string | null): Promise<{ revoked: string }>

  /** Public state of the operator's App. Both routes require a cookie session. */
  github(project: string, as?: string | null): Promise<ProjectGitHub>
  refreshGitHub(project: string, as?: string | null): Promise<ProjectGitHub>
  pushStatus(as?: string | null): Promise<PushStatus>
  registerPushDevice(args: PushRegistration, as?: string | null): Promise<{ device: PushDevice }>
  removePushDevice(id: string, as?: string | null): Promise<void>
  testPush(as?: string | null): Promise<PushTestResponse>
  pushPresence(here: boolean, as?: string | null): Promise<void>
  /** Registers this signed-in session's iPhone for notices; a new token replaces its old one. */
  registerPhone(args: PhoneRegistration, as?: string | null): Promise<{ device: PhoneDevice }>
  /** Forgets this session's iPhone. */
  forgetPhone(as?: string | null): Promise<void>
  /** Sends the server one of the web app's failures, for its log (Poulpe-706). */
  reportError(report: WebReport): Promise<void>
  /** A native attachment id or an imported file's SHA-256. */
  attachmentFile(id: string, as?: string | null): Promise<Blob>
  promptFile(id: string, as?: string | null): Promise<Blob>
  /** Outcomes since a moment (default 24 hours), at most seven days and 50 lines per group. */
  digest(project: string, options?: { since?: string; limit?: number }, as?: string | null): Promise<WorkDigest>
  snapshot(as?: string | null): Promise<Snapshot>
  /** Rejects with an ApiError coded `snapshot_needed` when the feed cannot continue from `since`. */
  changes(since: number, limit: number, as?: string | null, epoch?: string | null): Promise<ChangesPage>
  mutate(mutation: Mutation, as?: string | null): Promise<MutationResult>
  /** Rejects with a 401 without a session or a token. */
  me(): Promise<Me>
  setProfile(change: { name?: string; workspace?: string; plan_format?: PlanFormat }, as?: string | null): Promise<Me>
  signUpStart(args: SignUpArgs): Promise<Started<CreationOptions>>
  /** Makes the person, their passkey, their first workspace and a session. */
  signUpFinish(finish: Finish<RegistrationResponseJSON>): Promise<Me>
  /** Whether sign-up is closed now. */
  signUpStatus(): Promise<SignUpStatus>
  signInStart(): Promise<Started<RequestOptions>>
  /** Makes a session, in the person's oldest workspace for now. */
  signInFinish(finish: Finish<AuthenticationResponseJSON>): Promise<Me>
  /** Ends the cookie's session, if any, and clears the cookie. */
  signOut(): Promise<void>
  /** Starts a confirmation of `action`, for one of the signed-in person's passkeys. */
  confirmStart(action: ConfirmAction, as?: string | null): Promise<Started<RequestOptions>>
  /** Makes the confirmation, for five minutes and one use. */
  confirmFinish(finish: Finish<AuthenticationResponseJSON>, as?: string | null): Promise<Confirmed>
  /**
   * Sets whether the signed-in person confirms their actions with a passkey,
   * from their cookie session; answers as `me`. Turning it off while it is
   * on takes a confirmation of `stop_confirmations`, or is refused 403
   * `unconfirmed`; turning it on takes none.
   */
  setConfirmations(confirm: boolean, as?: string | null): Promise<Me>
  /** Mails a link that signs in or up, when the address may; answers alike either way. Without a session. */
  emailStart(args: EmailStartArgs): Promise<EmailStarted>
  /**
   * Mails a link that adds the address to the signed-in person. Refused, 403
   * `unconfirmed`, unless the session is ten minutes old at most or a live
   * confirmation of `add_email` (people/actions.ts) is the person's.
   */
  emailAddStart(args: EmailAddArgs, as?: string | null): Promise<EmailStarted>
  /** Signs in, up, or adds the address, as the link was asked for, and sets the session's cookie. 403 `email_link_refused` for a bad, spent or expired link. */
  emailFinish(args: EmailFinishArgs): Promise<Me>
  /** The signed-in person's passkeys. */
  passkeys(as?: string | null): Promise<PasskeyList>
  /** Starts adding a passkey: as fresh a session, or a confirmation of `add_passkey`, as adding an address; 403 `passkey_refused` at ten. */
  passkeyAddStart(as?: string | null): Promise<Started<CreationOptions>>
  /** Adds the passkey the browser made, and answers the person's passkeys. */
  passkeyAddFinish(finish: Finish<RegistrationResponseJSON>, as?: string | null): Promise<PasskeyList>
  /** Names the person's own passkey, or clears its name with null (1 to 64 characters otherwise); answers their passkeys. 404 for another's. */
  passkeyName(id: string, name: string | null, as?: string | null): Promise<PasskeyList>
  /**
   * Removes the person's own passkey and answers the rest. Refused, 403
   * `unconfirmed`, without a live confirmation of `remove_passkey` for it
   * (people/actions.ts), made in this session; 403 `last_passkey` for the
   * last one of a person with no email address.
   */
  passkeyRemove(id: string, as?: string | null): Promise<PasskeyList>
  /** The runner owner's current account limits; cookie-authenticated, never session tokens. */
  planLimits(runner: string, as?: string | null): Promise<PlanLimitsReply>
  checkProgress(as?: string | null): Promise<LiveCheck[]>
  /** A project's queue in detail, by its key or id: measured, never evidence or an order. */
  queueDetail?(project: string, as?: string | null): Promise<QueueDetail>
  /** The workspace's runners, revoked ones among them, with what each offers. */
  runners(as?: string | null): Promise<Runner[]>
  repositorySettings?(runner: string, query: RepositoryQuery): Promise<RunnerRepositorySettings>
  agentSettings?(runner: string): Promise<RunnerAgentSettings[]>
  setAgentSettings?(runner: string, change: AgentSettingsChange): Promise<RunnerAgentSettings[]>
  /** Edits or revokes one of a vendor's saved command rules on the runner (Poulpe-524). */
  changeCommandRule?(runner: string, change: CommandRuleChange): Promise<RunnerAgentSettings[]>
  runnerSettings?(runner: string): Promise<RunnerOwnSettings>
  setRunnerSettings?(runner: string, change: RunnerSettingsChange): Promise<RunnerOwnSettings>
  /** Each project's queue-cache health, read live by the runner's owner. */
  cacheHealth?(runner: string): Promise<QueueCache[]>
  /** The local models of the runner's machine, read live by its owner (Poulpe-447). */
  localModels?(runner: string): Promise<LocalModels>
  /** Downloads, checks or cancels one of them: the models as they stand once it is taken. */
  localModel?(runner: string, action: LocalModelAction): Promise<LocalModels>
  /** Makes a code that registers one runner in the signed-in person's workspace, with a live confirmation of `register_runner`. */
  runnerCode(as?: string | null): Promise<RunnerCode>
  /** A page of a session's transcript: the items after the item `after`, or from its start. */
  transcript(session: string, after: string | null, as?: string | null): Promise<TranscriptPage>
  /** The newest page of a session's transcript before the item `before`, or before its end with `end` (Poulpe-843). */
  transcriptBefore(session: string, before: string, as?: string | null): Promise<TranscriptPage>
  /** A kept diff; 404 when this file has none. */
  sessionChangesDiff(session: string, path: string, as?: string | null): Promise<SessionChangesDiff>
  /** A proposal's kept diff, or the ask for it while there is none. */
  proposalChanges(proposal: string, as?: string | null): Promise<ProposalChanges>
  /** One file's diff from a proposal's kept copy; 404 when there is none of it. */
  proposalChangesDiff(proposal: string, path: string, as?: string | null): Promise<ProposalChangesDiff>
  /** A plan with its body, its revisions and its comments; 404 for none in this workspace. */
  repoDocs(project: string, as?: string | null): Promise<RepoDocs>
  repoDoc(project: string, path: string, sha: string, as?: string | null): Promise<RepoDoc>
  plan(id: string, as?: string | null): Promise<PlanDetail>
  /** The descriptions an issue replaced, newest first, without their texts; 404 for none in this workspace. */
  issueRevisions(id: string, as?: string | null): Promise<IssueRevisions>
  /** One replaced description with its text. */
  issueRevision(id: string, number: number, as?: string | null): Promise<IssueRevision>
  /** Issues and plans whose words begin with the query's, ranked; a key or a plan's `#NNN` jumps first. */
  search(query: string, options: SearchOptions, as?: string | null): Promise<SearchAnswer>
}

export interface ProjectGitHub {
  configured: boolean
  repository: string | null
  installation_id: number | null
  snapshot: {
    app: { name: string; slug: string; installation_id: number | null; suspended: boolean; contents_write: boolean } | null
    checked_at: string | null
    problem: string | null
  }
}

/** Preferences persist only on the runner. Commands are instructions for its owner. */
export interface AgentPreferences {
  enabled: boolean
  program: string
  defaults: { model?: string; mode?: string; effort?: { id: string; value: string } }
}
export interface RunnerAgentSettings {
  agent: string
  preferences: AgentPreferences
  programs: string[]
  signed_in: boolean | null
  catalogue: AgentCatalogue | null
  sign_in: string | null
  update: string | null
  /** On the Claude row alone: whether Claude API's key is in that Mac's Keychain. The key never leaves it. */
  api_key_set?: boolean
  /** The catalogue was left out to keep the runner's reply within the message limit (Poulpe-618). */
  metadata_error?: 'invalid' | 'too_large'
  /** The vendor's saved command rules on this runner (Poulpe-524); left out by an older runner. */
  command_rules?: CommandRules
}
/**
 * Codex's user rules on the runner (`rules/default.rules`), which Codex owns.
 * Only the allow rules Poulpe recognises are listed; other lines are counted.
 */
export type CommandRules =
  | { state: 'rules'; version: string; rules: CommandRule[]; other_lines: number; running: number }
  | { state: 'unsupported' }
  | { state: 'unreadable' }
export interface CommandRule {
  /** Its line in the file, from 1. */
  line: number
  pattern: string[]
}
export interface CommandRuleChange {
  agent: string
  version: string
  line: number
  action: { kind: 'edit'; pattern: string[] } | { kind: 'revoke' }
}
export interface AgentSettingsChange {
  agent: string
  expected: AgentPreferences
  preferences: AgentPreferences
}
/** The runner's own settings, kept on it alone (Poulpe-498): how many of the sessions a person starts run at once. */
export interface RunnerOwnSettings {
  capacity: number
}
export interface RunnerSettingsChange {
  expected: RunnerOwnSettings
  settings: RunnerOwnSettings
}

/**
 * One project's queue-cache health on a runner (`poulpe_protocol::cache_health`,
 * Poulpe-561), read live from its disk: fixed codes, never a path. A cache
 * capacity refusal does not establish capacity for a disposable build.
 */
export interface QueueCache {
  project: string
  health: QueueCacheHealth
}
export type QueueCacheHealth =
  | { state: 'unused' }
  | { state: 'in_use' }
  | { state: 'unreadable' }
  | { state: 'ready'; uncertain: number; purposes: { landing: QueueCachePurpose; idle_main: QueueCachePurpose }; retained_invalid?: QueueCacheInvalid[]; storage?: QueueCacheStorage }
  | { state: 'exhausted' }
  | { state: 'malformed'; uncertain: number; entries: { generation: number; entry: QueueCacheEntry }[] }
  | { state: 'budget'; uncertain: number; uncertain_bytes: number; budget_bytes: number }
  | { state: 'capacity'; uncertain: number; retained_invalid: QueueCacheInvalid[]; available_bytes: number | null; required_bytes: number; source: 'important' | 'immediate' | 'immediate_fallback' | null; storage: QueueCacheStorage }

export type QueueCacheInvalid = { generation: number; entry: QueueCacheEntry }
export type QueueCacheUsage = { logical_bytes: number; allocated_bytes: number }
export type QueueCacheStorage = { quarantined: QueueCacheUsage | null; reusable: QueueCacheUsage; reusable_budget_bytes: number }
export type QueueCachePurpose = 'reusable' | 'fallback'
export type QueueCacheEntry = 'not_directory' | 'completion_hint' | 'cache_directory' | 'unreadable'

/**
 * The local models of a runner's machine (`poulpe_protocol::local_models`,
 * Poulpe-447), read live from it: the models Poulpe has tested, which the
 * runner pins, downloads and checks. Never a file, a folder or an address.
 */
export interface LocalModels {
  /** The machine's memory; 0 when the runner could not read it. */
  memory_bytes: number
  models: LocalModel[]
}
export interface LocalModel {
  id: string
  name: string
  /** The file's size. */
  bytes: number
  /** The most memory the model was seen to hold. */
  memory_bytes: number
  measured: { by_kind: { kind: LocalModelWork; accepted: [number, number] }[]; cases_per_kind: number; attempts_per_case: number }
  fit: 'fits' | 'tight' | 'too_big'
  state: 'absent' | 'unchecked' | 'checking' | 'downloading' | 'ready'
  /** Of a download: receiving, or reading back what was received. */
  phase: 'downloading' | 'checking' | null
  progress: { done: number; total: number } | null
  /** What a download would still bring. */
  download_bytes: number
  /** Why the last download did not give the file. */
  problem: 'no_space' | 'unreachable' | 'refused' | 'wrong_file' | 'failed' | null
}
export type LocalModelWork = 'exact_edit' | 'test_writing' | 'cross_file_search' | 'diff_review' | 'summary' | 'board_update_draft'
export interface LocalModelAction {
  kind: 'download' | 'check' | 'cancel'
  model: string
}

/** Live projection of the runner's local repository choices. */
export interface RepositoryPreferences {
  checkout: string | null
  root: string
  folder: string
  branch: string
  default_branch: string
  remote: string | null
}
export interface RepositoryInspection {
  folder: string
  repository: boolean
  has_commit: boolean
  remotes: { name: string; address: string }[]
  remote: string | null
  default_branch: string | null
}
export interface RunnerRepositorySettings {
  default_root: string
  preferences: RepositoryPreferences
  inspection: RepositoryInspection | null
}
export type RepositoryAction = { action: 'read' } | { action: 'inspect'; folder: string } | {
  action: 'save'; expected: RepositoryPreferences; preferences: RepositoryPreferences; initialise: boolean
}
export interface RepositoryQuery { project: string; action: RepositoryAction }

/** Measurements for display alone, from the runner's active check job. */
export interface LiveCheck {
  job: string
  project: string
  session: string | null
  proposal: string | null
  landing: string | null
  reported_at: string | null
  usual_ms: number | null
  progress: null | {
    signature: string
    /** Wall time since enqueue when activity is present. */
    elapsed_ms: number
    completed: number
    /** May be zero before parts are selected. */
    total: number
    part: string | null
    command_completed: number | null
    command_total: number | null
    /** Absent on older runners. Execution includes preparation and cleanup. */
    activity?: null | {
      phase: 'queued' | 'admitted' | 'preparing' | 'lease_acquired' | 'part_started' | 'part_ended' | 'finished'
      purpose: 'landing' | 'proposal' | 'hand' | 'idle_main'
      wait_ms: number
      execution_ms: number
      holder: string | null
      /** The urgency the runner applies to its place; absent when normal (Poulpe-680). */
      urgency?: Urgency
      /** How the check used a cache and where its time went (protocol 49, Poulpe-677). Absent on older runners. */
      measures?: CheckMeasures
    }
  }
}
export type CacheUse = 'reused' | 'new' | 'seeded' | 'unavailable' | 'none_by_design'
export type CacheReason =
  | 'no_cleanup' | 'purpose' | 'trimmed' | 'no_cache' | 'busy' | 'invalid' | 'no_idle' | 'empty' | 'capacity' | 'error'
export type PartOutcome =
  | 'passed' | 'failed' | 'timed_out' | 'stopped' | 'not_started' | 'runner_uncertain' | 'setup_failed' | 'reused'
/** Each fact is absent until the runner knows it. For display alone. */
export interface CheckMeasures {
  cache?: CacheUse
  cache_reason?: CacheReason
  /** From the end of waiting to the first attempt, or to cleanup when no part ran. */
  preparation_ms?: number
  cleanup_ms?: number
  /** `attempts` is 0 for a reused part, else 1: a failed part never runs again (Poulpe-750). */
  parts?: { part: string; outcome: PartOutcome; attempts: number; duration_ms: number }[]
}
/** A check's last live report, as the runner sent it: for display alone. */
export type CheckProgress = NonNullable<LiveCheck['progress']>
export type CheckPurpose = 'landing' | 'proposal' | 'hand' | 'idle_main'

/** Fastest, median and slowest, in milliseconds. */
export interface Spread { min_ms: number; median_ms: number; max_ms: number }

/**
 * What comparable finished checks measured (queue/detail.rs, History): passed
 * runs timed, and every run counted. `same_check` compares the same runner,
 * purpose and work; `same_purpose` any work of that runner and purpose, at low
 * confidence. `cache` counts the timed runs by how they used a cache; an older
 * runner's run counts as `unknown` (Poulpe-677).
 */
export interface CheckHistory {
  basis: 'same_check' | 'same_purpose'
  runs: number
  execution: Spread | null
  wait: Spread | null
  newest_at: string | null
  oldest_at: string | null
  passed: number
  /** A receipt says a part failed. */
  failed: number
  /** Ended otherwise without passing: stopped, refused, a runner's error or uncertain end, or a check by hand, whose part results are not kept typed. */
  unsuccessful: number
  confidence: 'none' | 'low' | 'steady'
  cache: { reused: number; new: number; seeded: number; unavailable: number; none_by_design: number; unknown: number }
}

/** A check job as the server knows it, with its history. */
export interface QueueCheck {
  job: string
  runner: string
  state: string
  sent_at: string
  purpose: CheckPurpose
  progress: CheckProgress | null
  reported_at: string | null
  history: CheckHistory
}

/** What the queue makes a proposal wait on, as its rules stand. */
export type QueueWait =
  | { on: 'head'; note: string | null }
  | { on: 'queue' }
  | { on: 'runner'; job: string; runner: string; state: string }
  | { on: 'admission'; job: string; holder: string | null }
  | { on: 'check'; job: string }
  /** The runner measured its check to its end; the job's outcome is still to come. */
  | { on: 'outcome'; job: string; runner: string }
  | { on: 'review'; session: string | null; note: string | null }
  | { on: 'landing'; landing: string }
  | { on: 'batch'; landing: string }
  | { on: 'held'; landing: string }
  | { on: 'forge'; landing: string }
  | { on: 'candidate'; landing: string; build: boolean | null }
  /** A person's passkey; a deferred landing waits for it outside the running slot (Poulpe-666). */
  | { on: 'approval'; landing: string; deferred: boolean }
  /** Its deferred landing, approved, waits for its turn to resume. */
  | { on: 'resume'; landing: string }

/** What the running or deferred landing says of a person's approval of its current candidate. */
export type ApprovalActual =
  | { state: 'undecided' }
  | { state: 'requested'; landing: string; since: string | null; deferred_at: string | null; reasons: PersonReason[]; reasons_total: number; digest: string; stale: boolean }
  | { state: 'satisfied'; landing: string; at: string | null; deferred_at: string | null; resume_at: string | null }
  | { state: 'not_required'; landing: string; at: string | null }

/** The current policy's rule on what is known before the candidate: a guide, never a decision. */
export interface ApprovalForecast {
  /** Null when it cannot tell. */
  needs_person: boolean | null
  reasons: PersonReason[]
  reasons_total: number
  policy_version: number
  diff: 'complete' | 'truncated' | 'missing'
  definitions: 'compared' | 'unknown' | 'not_needed'
}

export interface QueueIssue { id: string; key: string; priority: Priority; state: string }

/** An open proposal, and its landing when it is in one (queue/detail.rs, Item). */
export interface QueueItem {
  proposal: string
  issues: QueueIssue[]
  /** The highest of its issues': the queue's order does not read it. */
  priority: Priority
  session: string
  head: string
  stage: ProposalStep
  since: string | null
  created_at: string
  waits_on: QueueWait[]
  check: QueueCheck | null
  /** Recent landing checks on its runner, of any work, before it lands. */
  landing_history: CheckHistory | null
  landing: {
    id: string
    state: 'running' | 'waiting' | 'deferred'
    proposals: string[]
    picked: boolean
    created_at: string
    waiting_for: 'forge' | 'candidate' | 'full_check' | 'approval' | 'resume' | null
    waiting_since: string | null
    /** When it yielded the running slot, if deferred. */
    deferred_at: string | null
    /** Its place in the queue once approved while deferred. */
    resume_at: string | null
  } | null
  approval: { actual: ApprovalActual; forecast: ApprovalForecast }
  /** Open issues that block one it carries: the queue does not wait on them. */
  blocked_by: QueueIssue[]
  blocked_by_total: number
}

/** `GET /v1/projects/{project}/queue`: measured, never evidence or an order. */
export interface QueueDetail {
  project: string
  measured_at: string
  items: QueueItem[]
  items_total: number
  items_truncated: boolean
  active_checks: (QueueCheck & { session: string | null; proposal: string | null; landing: string | null })[]
  active_total: number
  active_truncated: boolean
  history_truncated: boolean
  /** The project's runners, with every check on their machines (Poulpe-779); absent from an older server. */
  machines?: QueueMachine[]
  /** How main's last finished check ended, for an idle strip. */
  main_check?: { sha: string; result: 'passed' | 'failed'; at: string | null } | null
}

/** One part of a check on a machine, as it stands. */
export interface MachinePart {
  name: string
  state: 'passed' | 'failed' | 'running' | 'to_come'
  /** Its time, once it ended. */
  duration_ms?: number | null
}

/**
 * One check in a slot of a runner's machine (queue/machines.rs). A check
 * whose session or job is another workspace's is `anonymous`: nothing names
 * it. A person's terminal is never named.
 */
export interface MachineCheck {
  state: 'running' | 'taking' | 'waiting'
  source: 'queue' | 'agent' | 'person' | 'unknown'
  via: 'run_check' | 'shell' | null
  anonymous: boolean
  session: string | null
  /** The session's agent's vendor. */
  vendor: string | null
  issue: { id: string; key: string; title: string } | null
  job: string | null
  purpose: CheckPurpose | null
  proposal: string | null
  /** The part asked for: `all` for the whole check. */
  part: string | null
  target: string | null
  parts: MachinePart[]
  count: { what: 'crates' | 'tests' | 'files'; done: number; total?: number | null; failed: number } | null
  waiting_since: string
  running_since: string | null
  part_since: string | null
  wait_limit_ms: number | null
  /** What comparable runs on its machine took (Poulpe-781); none without one. */
  estimate?: MachineEstimate | null
  /** A running check's estimated end. */
  estimated_end?: string | null
  /** A waiter's estimated start, when the holder and all ahead of it have estimates. */
  estimated_start?: string | null
}

/**
 * What comparable runs took: those that passed, of the same part and target,
 * that used a cache the same way. Never shown as a number without one.
 */
export interface MachineEstimate {
  runs: number
  run_ms: number
  /** The median time of the part it runs now, where they timed it. */
  part_ms?: number | null
  confidence: 'low' | 'steady'
}

/** A check that ended in a slot in the last hour (Poulpe-781). */
export interface MachineEnded {
  source: MachineCheck['source']
  via: MachineCheck['via']
  anonymous: boolean
  vendor: string | null
  issue: { id: string; key: string; title: string } | null
  purpose: CheckPurpose | null
  part: string | null
  target: string | null
  outcome: 'passed' | 'failed' | 'stopped' | 'gave_up'
  queued_at: string
  started_at: string | null
  ended_at: string
}

/** A slot of a machine: what holds it, then its line in order. */
export interface MachineSlot {
  name: string
  running: MachineCheck[]
  waiting: MachineCheck[]
  /** The checks that ended in it in the last hour, oldest first; absent from an older server. */
  recent?: MachineEnded[]
}

/** A runner of the project, and its machine's checks as it last reported them. */
export interface QueueMachine {
  runner: string
  name: string
  connected: boolean
  last_seen_at: string | null
  /** Null when it sent no report: an older runner, or one just connected. */
  reported_at: string | null
  /** The one-minute load average, times 100. */
  load_centi: number | null
  cpus: number | null
  disk_free_bytes: number | null
  slots: MachineSlot[]
}
/** Repository docs at the runner's last fetched default branch commit. */
export interface RepoDocSource {
  source_sha: string
  branch: string
  repository: string | null
  read_at: string
  truncated: boolean
}
export interface RepoDocRow { path: string; title: string }
export interface RepoDocs { source: RepoDocSource | null; docs: RepoDocRow[] }
export interface RepoDoc extends RepoDocRow { body: string; kind: 'markdown' | 'html'; source_sha: string; url: string }
